/**
 * BOXING DRILLS DATA STRUCTURE - CONSOLIDATED
 *
 * This file contains consolidated boxing drills organized into skill levels:
 * 1. Beginner Boxing Basics (30 minutes) - Basic boxing stance and punches
 * 2. Intermediate Boxing Basics (45 minutes) - More boxing techniques and combinations
 * 3. Advanced Boxing Basics (45 minutes) - Challenging boxing techniques and coordination
 * 4. Beginner Boxing Footwork (30 minutes) - Basic boxing movement and footwork
 * 5. Intermediate Boxing Footwork (45 minutes) - More complex boxing footwork
 * 6. Advanced Boxing Footwork (45 minutes) - Advanced boxing movement and coordination
 * 7. Beginner Boxing Defense (30 minutes) - Basic defensive movements and blocks
 * 8. Intermediate Boxing Defense (45 minutes) - More defensive techniques and combinations
 * 9. Advanced Boxing Defense (45 minutes) - Advanced defensive techniques and coordination
 * 10. Complete Boxing (60 minutes) - All boxing skills combined
 *
 * DRILL STRUCTURE:
 * - id: Numeric ID starting from 2090 (boxing drills)
 * - name: Clean drill name (no emojis - for TTS audio generation)
 * - categories: Array of skill categories
 * - sports: Array containing 'boxing'
 * - difficulty: 'beginner', 'intermediate', or 'advanced'
 * - skill: High-level skill category (e.g., 'Basics', 'Footwork', 'Defense')
 * - points: Point value for completion (30-60 points based on time)
 * - instructions: Detailed explanation including drill name introduction
 * - timedInstructions: Array of {time, text} objects for SSML generation
 * - timeTotal: Total duration in seconds (1800-3600 seconds = 30-60 minutes)
 * - equipment: Required equipment (e.g., 'None' or 'Space to move')
 *
 * AUDIO GENERATION NOTES:
 * - Instructions field contains drill name introduction
 * - timedInstructions start directly with action steps
 * - SSML breaks are calculated based on time gaps between instructions
 * - Voice used: en-US-Studio-M (male Studio voice that supports SSML)
 *
 * BOXING DRILL REQUIREMENTS:
 * - All drills doable at home/indoors with minimal space
 * - Focus on individual skills (no partner drills)
 * - Equipment limited to space to move around
 * - Appropriate for 6-10 year olds
 * - Focus on shadow boxing and technique only
 * - No contact, no bags, no sparring
 * - Progressive skill development within each drill
 * - Emphasis on proper form and technique
 * - Kid-friendly language (no complex boxing terms)
 * - Safe movements only
 *
 * CREATED: [Date] - Boxing drills with SSML audio generation
 * UPDATED: [Date] - Consolidated into skill-based structure with longer drills
 */

const boxingDrills = [
  // BEGINNER BOXING BASICS (30 minutes) Done
  {
    id: 2090,
    name: 'Beginner Boxing Basics',
    categories: ['Basics', 'beginner'],
    sports: ['boxing'],
    difficulty: 'beginner',
    skill: 'Basics',
    points: 18,
    instructions:
      "Get ready to become a boxing superhero! This awesome workout teaches you how to stand, guard, and throw super-fast punches like a champion. We'll learn the Jab, the Cross, and the Hook in a fun, non-stop adventure. Let's move!",
    timedInstructions: [
      {
        time: 0,
        text: "Hello Champion! Welcome to your first boxing lesson. My name is Coach, and today, we're going to learn how to move and punch like a pro. Are you ready?",
      },
      {
        time: 8,
        text: "Excellent! Let's warm up. Every boxer starts by getting light on their feet. Just get on your toes and bounce gently. Boing, boing, boing.",
      },
      {
        time: 16,
        text: "Now, let's practice our footwork. We're going to shuffle forward and backward for 30 seconds. Take two small steps forward... then two small steps back. Go!",
      },
      {
        time: 25,
        text: "That's it! Stay on your toes. Never let your feet cross. Forward... and back. You're moving like a real boxer!",
      },
      {
        time: 35,
        text: 'Keep that rhythm. Ten seconds left. Small, quick steps. 5... 4... 3... 2... 1!',
      },
      {
        time: 47,
        text: "Great! Now, let's go side-to-side for 30 seconds. Two shuffles to your right... then two shuffles to your left. Keep your feet apart. Go!",
      },
      {
        time: 56,
        text: "Perfect! You're gliding! A good boxer is always moving, always hard to hit. Halfway there!",
      },
      {
        time: 67,
        text: 'Ten more seconds of shuffling! Stay light! 5... 4... 3... 2... 1!',
      },
      {
        time: 78,
        text: "Awesome footwork! Now, stand with your feet shoulder-width apart and put your hands on your hips. We're going to do torso twists to warm up our power muscles.",
      },
      {
        time: 89,
        text: "Twist your body gently from side to side. Look where you're twisting. This is where all your punching power comes from!",
      },
      {
        time: 99,
        text: "Let's do ten more twists, counting together! 10, 9, 8, 7, 6, 5... 4... 3... 2... 1!",
      },
      {
        time: 112,
        text: "Great! Now, let's warm up our arms with some shadow boxing. Just stand in place and punch slowly into the air. No power yet, just nice and easy.",
      },
      {
        time: 122,
        text: 'Move your arms, one after the other. Keep breathing. We are just waking up those punching muscles. Ten more seconds.',
      },
      {
        time: 133,
        text: "Excellent warm-up! Okay, time to learn the boxer's stance. Put your favorite foot a little bit behind you, like you're on a skateboard.",
      },
      {
        time: 142,
        text: 'Perfect. Now, bend your knees a little bit. This makes you a smaller target and gives you balance.',
      },
      {
        time: 150,
        text: 'Now for the most important part: your guard! Make two soft fists and bring them right up to your cheeks, with your elbows tucked in.',
      },
      {
        time: 159,
        text: "This is your superhero shield! It keeps you safe. Let's practice bouncing in our stance. Keep those hands glued to your cheeks!",
      },
      {
        time: 169,
        text: "Okay, our first punch is the JAB. It's the fastest punch, and it uses your front hand. Let's practice the motion slowly.",
      },
      {
        time: 179,
        text: "From your guard, punch your front hand straight out, and SNAP it right back to your cheek like it's on a rubber band. Let's do two more slowly. Out and back. Out and back. Perfect.",
      },
      {
        time: 190,
        text: "Alright, 30-second Jab drill! We're going to practice that move for the whole time. Front hand only! Let's see that speed! 3, 2, 1, Go!",
      },
      {
        time: 197,
        text: "Let's find a rhythm! Snap it out and back! 1, 2, 1, 2! Remember to keep your other hand right by your cheek, don't let that guard drop!",
      },
      {
        time: 206,
        text: "Great speed! You're halfway there! Keep breathing out with every single punch. Make a little 'whoosh' sound. That's it! You're doing great!",
      },
      {
        time: 215,
        text: "Ten seconds left! Faster, faster, you're a blur! Let's count the last five together! FIVE! FOUR! THREE! TWO! ONE!",
      },
      {
        time: 223,
        text: 'TIME! Awesome job! Next up is your power punch: the CROSS. This uses your back hand.',
      },
      {
        time: 231,
        text: "As you punch your back hand straight out, you MUST twist your body and pivot on your back foot, like you're squishing a bug on the floor. That's the secret to power.",
      },
      {
        time: 242,
        text: "Let's try it slowly. From your guard, twist your body and punch that back hand straight out. Then bring it right back. Again, twist and punch!",
      },
      {
        time: 253,
        text: 'Okay, 30-second power Cross drill! Back hand only! Ready? 3, 2, 1, Go!',
      },
      {
        time: 259,
        text: "Twist and punch! Feel the power come from your feet all the way to your fist. Let's count four of them! ONE! TWO! THREE! FOUR!",
      },
      {
        time: 268,
        text: "Yes! Remember to squish that bug on the floor every time! And keep your other hand glued to your cheek! Guard up! Let's do four more! ONE! TWO! THREE! FOUR!",
      },
      {
        time: 278,
        text: "Excellent! Ten seconds left! Give me your strongest punches yet! Let's count them down! 5... 4... 3... 2... 1!",
      },
      {
        time: 286,
        text: "TIME! Fantastic! Now, let's combine them for the famous one-two combo!",
      },
      {
        time: 293,
        text: "It's your fast Jab with your front hand, immediately followed by your power Cross with your back hand. Let's try it slow. Jab... Cross... Bam-BAM!",
      },
      {
        time: 304,
        text: 'Okay, this is our longest drill yet! 45 seconds of the one-two combo! I know you can do it! Ready? Go!',
      },
      {
        time: 311,
        text: "Front hand, back hand! Find that rhythm! Snap the front hand out, then drive the back hand forward with a twist! Let's count! ONE-TWO! ONE-TWO!",
      },
      {
        time: 321,
        text: "Keep it sharp! Don't forget to breathe! A quick breath on the jab, a bigger one on the cross! Whoosh-WHOOSH! You're doing great!",
      },
      {
        time: 331,
        text: "We're past the halfway point! This is where we build our stamina! Keep those hands coming right back to your cheeks after every punch. Stay protected!",
      },
      {
        time: 341,
        text: "Almost there! Ten seconds left! Let's see your fastest combos! Give me everything you've got! Let's count it down! 5... 4... 3... 2... 1!",
      },
      {
        time: 351,
        text: "TIME! Excellent! You've mastered the basics! Time for our Final Burnout Round!",
      },
      {
        time: 359,
        text: "We're going to do short drills of each punch, back to back, with no rest! First up, 20 seconds of JABS with your front hand. Ready? GO!",
      },
      {
        time: 367,
        text: "Just the front hand, snapping out and back! Let's count! 1, 2, 3, 4, 5! Keep that pace! Don't slow down! Ten seconds left! Faster, faster!",
      },
      {
        time: 379,
        text: 'Great! No rest, right into 20 seconds of the CROSS! Back hand, twist and punch! Go! Show me that power! Remember to squish the bug every single time!',
      },
      {
        time: 389,
        text: "Let's count five power punches! ONE! TWO! THREE! FOUR! FIVE! Ten seconds left, you're almost there! Keep going!",
      },
      {
        time: 399,
        text: "Awesome! Now for the final challenge! 30 seconds of the one-two COMBO, and we're going to add a superhero switch halfway through! Ready? Go!",
      },
      {
        time: 408,
        text: "Front hand, back hand! Bam-BAM! Keep that rhythm! You're in your champion round! Get ready to switch in 3... 2... 1...",
      },
      {
        time: 416,
        text: "SUPERHERO SWITCH! JUMP and switch your feet! Awesome! Now your other hand is in front! Let's try the combo from here! New front hand, new back hand!",
      },
      {
        time: 426,
        text: "It feels different, but you can do it! Ten seconds left! Give it everything you've got! 5... 4... 3... 2... 1!",
      },
      {
        time: 435,
        text: 'TIME! YOU DID IT! AMAZING JOB, CHAMPION! You were incredible!',
      },
      {
        time: 442,
        text: "Whew! Time to cool down. Let's start by taking a big, slow deep breath in...",
      },
      {
        time: 449,
        text: "And let it all out. Great. Let's do that one more time. Big breath in... and out.",
      },
      {
        time: 457,
        text: "Now, let's stretch our arms. Take one arm and gently pull it across your body. Give it a little hug.",
      },
      {
        time: 465,
        text: 'You should feel a nice stretch in your shoulder. You worked those arms hard today.',
      },
      {
        time: 472,
        text: "Perfect. Now let's switch to the other arm. Gently pull it across and hold.",
      },
      {
        time: 479,
        text: 'Great job. Clasp your hands behind your back and push your chest out. This stretches out your chest muscles.',
      },
      {
        time: 487,
        text: "And now, relax. Let's do the big shake out! Shake your arms! Shake your legs!",
      },
      {
        time: 494,
        text: 'Shake your whole body! You were fantastic today. You learned how to move and punch like a real boxer.',
      },
      {
        time: 503,
        text: 'You should be very proud! Great work today, champ! See you next time!',
      },
    ],
    timeTotal: 1055,
    equipment: 'None',
  },

  // INTERMEDIATE BOXING BASICS (45 minutes)
  {
    id: 2091,
    name: 'Intermediate Boxing Basics',
    categories: ['Basics', 'intermediate'],
    sports: ['boxing'],
    difficulty: 'intermediate',
    skill: 'Basics',
    points: 45,
    instructions:
      "Time for Level 2! This workout builds on your skills by teaching you the powerful Hook punch, how to block like a pro, and how to put it all together in awesome new combinations. Let's get to work, champ!",
    timedInstructions: [
      {
        time: 0,
        text: "Hello Champion, and welcome back! You mastered the basics, so today we're moving on to the intermediate skills. Let's level up!",
      },
      {
        time: 8,
        text: "First, our warm-up. Get into your boxer's stance and start bouncing on your toes. Let's get that rhythm back.",
      },
      {
        time: 15,
        text: "Now, let's add some head movement. As you bounce, dip your shoulders from side to side. Imagine you're dodging laser beams. Dodge left... dodge right.",
      },
      {
        time: 25,
        text: "Keep dodging! This is called 'slipping' and it makes you hard to hit. 10 more seconds, stay quick!",
      },
      {
        time: 36,
        text: "Great! Now let's do some faster footwork. Shuffle forward, then shuffle back. Then shuffle left, then shuffle right. Keep moving for 30 seconds. Go!",
      },
      {
        time: 46,
        text: "That's it! Stay on your toes. A great boxer has great feet. Halfway there!",
      },
      {
        time: 56,
        text: 'Ten seconds left! Quick feet, quick feet! 5... 4... 3... 2... 1...',
      },
      {
        time: 67,
        text: "Awesome! Now, let's warm up those punches with some light shadow boxing. Get your guard up by your cheeks.",
      },
      {
        time: 75,
        text: "Let's warm up those punches with some light shadow boxing. Get your guard up by your cheeks and let's throw some easy Jabs and Crosses for 20 seconds. Ready? Go!",
      },
      {
        time: 82,
        text: 'Nice and easy. Just let your arms flow. Punch your front hand straight out... then your back hand with a little twist. We are just waking up the muscles.',
      },
      {
        time: 90,
        text: 'Five more seconds of easy shadow boxing... 5, 4, 3, 2, 1. Great!',
      },
      {
        time: 96,
        text: "Fantastic warm-up! Okay, now for a 30-second drill to perfect our one-two combo. That's the front-hand Jab and the back-hand Cross.",
      },
      {
        time: 105,
        text: "We'll do 15 seconds on this side, then we'll switch our feet for a fun challenge! Ready? 3, 2, 1, Go!",
      },
      {
        time: 112,
        text: "Front hand snaps out for the Jab, back hand twists for the Cross! Let's count them! ONE-TWO! Again! ONE-TWO! Keep those hands high!",
      },
      {
        time: 120,
        text: 'Perfect! Okay, get ready... SUPERHERO SWITCH! JUMP and switch your feet, so your other foot is in front! Awesome!',
      },
      {
        time: 128,
        text: "Now your hands have new jobs! Let's try the one-two from this side! New front hand for the Jab, new back hand for the Cross! Let's go!",
      },
      {
        time: 136,
        text: "It feels a little weird, right? That's okay, you're training your whole body! Let's do a few more! Jab-Cross! You're doing great! 5... 4... 3... 2... 1...",
      },
      {
        time: 146,
        text: "TIME! Great refresher and awesome job with the switch! Now, it's time to learn a new punch: the HOOK.",
      },
      {
        time: 147,
        text: "The hook is a side punch. We'll use our front arm. Bring your front elbow up and make your arm into an 'L' shape, like a pirate's hook.",
      },
      {
        time: 156,
        text: "Keeping that 'L' shape, you swing your arm across in front of you. Let's try it slowly. Front arm up into an 'L'... and swing it across. Whoosh!",
      },
      {
        time: 164,
        text: "Let's do one more slowly to get the feel. Arm up... swing... and bring it right back to your cheek. Perfect.",
      },
      {
        time: 170,
        text: "Okay, 30-second Hook drill! We're going to practice that move for the whole time. Front arm only! Ready? 3, 2, 1, Go!",
      },
      {
        time: 176,
        text: "Let's throw some hooks! Remember, arm in that 'L' shape, swing it across, and bring it right back to your cheek. Let's count four of them! ONE! TWO! THREE! FOUR!",
      },
      {
        time: 185,
        text: "Great! Keep practicing. Make sure your other hand is glued to your cheek, protecting you! Don't let that guard drop! Now give me four more! ONE! TWO! THREE! FOUR!",
      },
      {
        time: 194,
        text: "Awesome! You're halfway there! Keep that body rotating and your feet light. Don't let your elbow droop down, keep that pirate hook shape strong through the whole punch! Let's do four more!",
      },
      {
        time: 198,
        text: "Excellent! We have ten seconds left! This is where we get stronger! Give me your best ones yet! Let's count them down! 5... 4... 3... 2... 1!",
      },
      {
        time: 199,
        text: "TIME! Awesome! Now you have three punches! Let's build our new combo: the 1-2-3! That's a Jab, a Cross, and a Hook!",
      },
      {
        time: 210,
        text: "So it's: front hand punch... back hand punch with a twist... then front arm pirate hook! Let's try it slow. Jab... Cross... Hook.",
      },
      {
        time: 222,
        text: "Okay, 45-second combo drill on the 1-2-3! Let's see it! Ready? Go!",
      },
      {
        time: 229,
        text: 'Front hand, back hand, front hook! Find that flow!',
      },
      {
        time: 236,
        text: "Remember all your form! Quick snap on the jab, big twist on the cross, strong 'L' on the hook!",
      },
      {
        time: 246,
        text: "You're doing great, this is a tough combo! Keep those hands coming back to your guard!",
      },
      {
        time: 255,
        text: 'Ten seconds left! Show me your best one! 5... 4... 3... 2... 1...',
      },
      {
        time: 267,
        text: "TIME! Fantastic! Now that you're an offensive machine, let's learn some defense. We're going to learn how to block.",
      },
      {
        time: 277,
        text: "It's simple. From your guard, if a punch is coming at your face, you just bring your glove up to your forehead to block it. It's like you're answering a secret agent phone on your wrist.",
      },
      {
        time: 289,
        text: "Let's practice that. I'm going to shout 'Block!' and you bring your front hand to your forehead. Ready? BLOCK!",
      },
      {
        time: 296,
        text: 'Good! Back to guard. Now the other hand. BLOCK! Good! Back to guard. BLOCK! BLOCK!',
      },
      {
        time: 305,
        text: "Now for the fun part. After you block, you can throw a punch back! We're going to practice blocking with our front hand, then firing a power Cross with our back hand.",
      },
      {
        time: 317,
        text: "So the combo is: Block... Cross! Let's try it slowly. Front hand to forehead... then back hand punch with a twist. Block... Cross! Front hand to forehead... then back hand punch with a twist. Block... Cross!",
      },
      {
        time: 328,
        text: "Alright, 30-second defensive drill! We're practicing the Block and Cross. Get ready to defend and attack! 3, 2, 1, Go! Block... Cross! Front hand to forehead... then back hand punch with a twist. Switch feet and do it again! Block... Cross! Block... Cross! Block... Cross! Front hand to forehead... then back hand punch with a twist. Swith feet! Block... Cross! Block... Cross! Block... Cross!",
      },
      {
        time: 343,
        text: "Let's find a rhythm! Block... Cross! Again! Block... Cross! Keep that back hand fast and powerful!",
      },
      {
        time: 350,
        text: 'Halfway there! Make sure after you punch, both hands come right back to your cheeks! Always stay protected! Swith feet. Front hand to forehead... then back hand punch with a twist. Swith feet! Block... Cross! Block... Cross! Block... Cross!',
      },
      {
        time: 357,
        text: "Ten seconds left! Let's do three more, super sharp! Block-Cross! That's one! Block-Cross! That's two! One more! Block-Cross! Three! Swith feet! Block... Cross! Block... Cross! Block... Cross! Front hand to forehead... then back hand punch with a twist. Swith feet! Block... Cross! Block... Cross! Block... Cross!",
      },
      {
        time: 366,
        text: "TIME! That is how a champion fights! Offense and defense! It's time for our Final Burnout Round!",
      },
      {
        time: 368,
        text: "It's time for our 60-second burnout round! I'll call out the moves, and you do them as fast as you can! Starting with the one-two combo! Remember, that's your front hand snapping straight out for the Jab, then your back hand immediately punching straight out with a power twist for the Cross! JAB-CROSS! Go! Front hand jab, back hand cross, twist! Another... front hand snapping straight out for the Jab, then your back hand immediately punching straight out with a power twist for the Cross!",
      },
      {
        time: 378,
        text: "Okay, now a front-hand HOOK! Remember to make that 'L' shape with your arm and swing it across! Go!",
      },
      {
        time: 384,
        text: 'Awesome hook! Back to the one-two! Snap that front hand out for the Jab, then the back hand with a power twist for the Cross!',
      },
      {
        time: 392,
        text: "That's it! Jab-Cross! Keep that rhythm! Bam-BAM! Do it again!",
      },
      {
        time: 398,
        text: "Time to dodge! Dip your shoulder to the left, then to the right! Like you're dodging laser beams! Go! Left, right!",
      },
      {
        time: 405,
        text: "Great head movement! Now the big combo, the 1-2-3! That's your front Jab... your back Cross... and finish with that front-arm Hook!",
      },
      {
        time: 414,
        text: "Yes! Front punch, back punch, front hook! You're a combo machine!",
      },
      {
        time: 420,
        text: 'Get ready to defend! BLOCK! Bring your front hand straight up to your forehead like a shield! Ready? BLOCK! Now switch your feet and do it again! BLOCK! Switch feet again! BLOCK!',
      },
      {
        time: 426,
        text: 'Perfect block! Now fire back with a power CROSS! The combo is: Block... then PUNCH! Defend and attack! Switch your feet and do it again! BLOCK! PUNCH! Switch feet again! BLOCK! PUNCH!',
      },
      {
        time: 434,
        text: 'Awesome! Back to footwork! Quick shuffles! Two steps forward, two steps back! Stay on your toes!',
      },
      {
        time: 441,
        text: 'Keep moving, keep moving! A moving target is hard to hit! You look like a pro!',
      },
      {
        time: 412,
        text: 'Ten seconds! Freestyle! Throw any punches you want! Go, go, go! 10... 9... 8... 7... 6...',
      },
      {
        time: 421,
        text: '5... 4... 3... 2... 1! TIME! YOU ARE A TRUE CHAMPION!',
      },
      {
        time: 429,
        text: "Incredible work today. Let's cool down. Start with a big, slow deep breath in...",
      },
      {
        time: 436,
        text: "And let it all out. Fantastic. Let's do that one more time. Big breath in... and out.",
      },
      {
        time: 444,
        text: "Let's stretch those arms. Take one arm and gently pull it across your body, giving it a hug.",
      },
      {
        time: 452,
        text: 'You learned so many new moves today. Your brain and body worked hard.',
      },
      {
        time: 459,
        text: "Perfect. Now let's switch to the other arm. Gently pull it across and hold.",
      },
      {
        time: 466,
        text: 'Great job. Clasp your hands behind your back and push your chest out to stretch your chest muscles.',
      },
      {
        time: 474,
        text: "And relax. Let's do the big shake out! Shake your arms! Shake your legs!",
      },
      {
        time: 481,
        text: 'You were amazing today. You added the Hook and defensive blocking to your skills. You are a complete boxer!',
      },
      {
        time: 490,
        text: 'Be proud of your hard work! Great job today, champ! See you for the next level!',
      },
    ],
    timeTotal: 1059,
    equipment: 'None',
  },
  // ADVANCED BOXING BASICS (45 minutes) Done
  {
    id: 2092,
    name: 'Advanced Boxing Basics',
    categories: ['Basics', 'advanced'],
    sports: ['boxing'],
    difficulty: 'advanced',
    skill: 'Basics',
    points: 30,
    instructions:
      'Advanced Boxing Basics teaches you fancy boxing moves and tricky coordination. This 30-minute session helps you practice cool boxing moves, fun combinations, and expert coordination exercises.',
    timedInstructions: [
      {
        time: 0,
        text: "Welcome back, Champion! You've mastered the intermediate skills, and now it's time to learn the advanced secrets of boxing. This is where you become a true pro. Let's get to work!",
      },
      {
        time: 9,
        text: "Let's start with a high-energy warm-up. Get in your stance, guard up, and start bouncing on your toes. We're going to do 30 seconds of fast shadow boxing. Jabs and Crosses. Go!",
      },
      {
        time: 18,
        text: "That's it! Hands fast, feet light! Breathe out with every punch! Let's see that one-two combo! Bam-BAM!",
      },
      {
        time: 28,
        text: 'Ten seconds left! Pick up the pace! Faster, faster, faster! 5... 4... 3... 2... 1...',
      },
      {
        time: 39,
        text: "Great! Now let's warm up our defense. For 30 seconds, we're going to practice slipping. Just dip your shoulders side to side, like you're dodging laser beams. Go!",
      },
      {
        time: 49,
        text: "Slip left, slip right. Keep your hands glued to your cheeks. Bend at your knees, not your back. You're making yourself a smaller target!",
      },
      {
        time: 60,
        text: 'Ten seconds left! Quick slips! Quick, quick, quick! 5... 4... 3... 2... 1...',
      },
      {
        time: 70,
        text: "Fantastic! Now, let's do a quick drill of our toughest combo so far: the 1-2-3. That's Jab, Cross, and Hook. 30 seconds, let's go!",
      },
      {
        time: 79,
        text: 'Front hand snap, back hand twist, front arm pirate hook! Find that flow!',
      },
      {
        time: 88,
        text: "Remember all your form! Keep it sharp! This is just the warm-up for the new moves we're about to learn!",
      },
      {
        time: 97,
        text: 'Ten seconds! Give me two more perfect combos! ONE! and TWO! Great work!',
      },
      {
        time: 105,
        text: "Okay, it's time to add our fourth and final punch: the UPPERCUT. This is an awesome power punch.",
      },
      {
        time: 114,
        text: "To throw an uppercut with your back hand, you dip your knees down, and then explode upwards, punching straight up as if you're trying to punch a giant on the chin. It's like a scoop!",
      },
      {
        time: 125,
        text: "Let's try it slowly. Bend your knees... and drive that back fist straight up! Whoosh! Let's do a few more. Bend... and explode up!",
      },
      {
        time: 136,
        text: 'Alright, 30-second Uppercut drill! Back hand only! Ready? 3, 2, 1, Go!',
      },
      {
        time: 142,
        text: "Bend and punch! The power comes from your legs! Let's count them! ONE! TWO! THREE!",
      },
      {
        time: 150,
        text: "Keep your other hand protecting your cheek! Don't lean too far forward. Drive straight up!",
      },
      {
        time: 158,
        text: 'Ten seconds left! Give me five powerful uppercuts! 5! 4! 3! 2! 1!',
      },
      {
        time: 167,
        text: "TIME! Awesome power! Now let's learn our final defensive move: the ROLL. It's like a super-dodge.",
      },
      {
        time: 175,
        text: "To do a roll, you're going to draw the letter 'U' with your head. Bend your knees, roll down and under an imaginary punch, and come up the other side. Let's try it.",
      },
      {
        time: 186,
        text: "Roll to your right... and come up. Now roll to your left... and come up. It's like a bob and weave! Keep your hands up the whole time.",
      },
      {
        time: 196,
        text: "Okay, let's put it all together. Time for our advanced combos. First up is the Jab, Slip, Cross. This is how you punch, dodge, and punch back.",
      },
      {
        time: 206,
        text: "The combo is: You throw a fast Jab... then you slip or dodge to the side... then you fire back with your power Cross. Let's try it slow. Jab... Slip... Cross.",
      },
      {
        time: 218,
        text: 'Okay, 45-second Jab-Slip-Cross drill! This needs focus! Ready? Go!',
      },
      {
        time: 224,
        text: 'Throw your front-hand Jab! Now slip to the side, dodging the counter-punch! Now fire back with your back-hand Cross!',
      },
      {
        time: 233,
        text: "Let's find the rhythm. Punch, dodge, punch! Jab, Slip, Cross! You're fighting like a real strategist!",
      },
      {
        time: 243,
        text: "Keep those hands high, especially when you slip! Don't get lazy with your guard! You're past halfway!",
      },
      {
        time: 252,
        text: "Ten seconds left! Let's see two more perfect ones! Jab-Slip-Cross! That's one! Jab-Slip-Cross! That's two!",
      },
      {
        time: 263,
        text: 'TIME! That was a pro-level combination! Now for a power combo using our new punch: the 1-2-Uppercut-2.',
      },
      {
        time: 272,
        text: "It goes: front Jab... back Cross... back Uppercut... and another back Cross! Let's try it slow. Jab... Cross... Uppercut... Cross.",
      },
      {
        time: 284,
        text: "Okay, 45-second power combo drill! This is a tough one! Let's see it! Go!",
      },
      {
        time: 290,
        text: 'Front Jab! Back Cross! Now bend and drive up with that back Uppercut! And finish with another power Cross!',
      },
      {
        time: 299,
        text: "Find that flow! Punch, punch, scoop, punch! It's all about rhythm and twisting your body!",
      },
      {
        time: 308,
        text: "This is a real power-building combo! You're past halfway! Don't let your guard drop!",
      },
      {
        time: 317,
        text: 'Ten seconds left! Give me one more perfect, powerful combo! Go! Jab-Cross-Uppercut-Cross!',
      },
      {
        time: 329,
        text: "TIME! Unbelievable! You've mastered all the advanced basics! It's time for the Champion's Minute!",
      },
      {
        time: 338,
        text: "60 seconds, non-stop! I call it, you do it! All your skills! Ready? Let's start with a JAB-CROSS-HOOK! Go!",
      },
      {
        time: 347,
        text: 'Great! Now SLIP LEFT, SLIP RIGHT! Fire back with an UPPERCUT! Awesome!',
      },
      {
        time: 355,
        text: 'Give me a one-two! JAB-CROSS! Now ROLL under a punch! Perfect!',
      },
      {
        time: 363,
        text: "Let's see that defensive combo! BLOCK! And fire a CROSS back!",
      },
      {
        time: 370,
        text: 'SUPERHERO SWITCH! Switch your feet! Throw a one-two from the other side!',
      },
      {
        time: 378,
        text: 'Switch back! Ten seconds left! Freestyle! Throw every punch you know! Go, go, go!',
      },
      {
        time: 388,
        text: '5... 4... 3... 2... 1... TIME! YOU ARE AN ADVANCED BOXER! INCREDIBLE WORK!',
      },
      {
        time: 397,
        text: "That was amazing. Let's cool down. Take a slow, deep breath in...",
      },
      {
        time: 403,
        text: "And let it all out. You've earned this rest. One more time, big breath in... and out.",
      },
      {
        time: 411,
        text: "Let's stretch our arms. Take one arm and gently pull it across your body, giving it a hug.",
      },
      {
        time: 418,
        text: "Perfect. Now let's switch to the other arm. Gently pull it across and hold.",
      },
      {
        time: 425,
        text: "Now, let's stretch your back from all those rolls. Stand up straight and slowly roll your chin to your chest.",
      },
      {
        time: 433,
        text: 'Great job. Clasp your hands behind your back and push your chest out to stretch your chest muscles.',
      },
      {
        time: 440,
        text: "And relax. Let's do the big shake out! You've earned it! Shake out your arms and legs!",
      },
      {
        time: 447,
        text: 'You were a true champion today. You learned every punch in the book and how to defend yourself like a pro. Be proud!',
      },
      {
        time: 456,
        text: "Great work today, champ! You've completed the basics of boxing!",
      },
    ],
    timeTotal: 1121,
    equipment: 'None',
  },
  // BEGINNER BOXING FOOTWORK (30 minutes)
  {
    id: 2093,
    name: 'Beginner Boxing Footwork',
    categories: ['Footwork', 'beginner'],
    sports: ['boxing'],
    difficulty: 'beginner',
    skill: 'Footwork',
    points: 30,
    instructions:
      'Beginner Boxing Footwork teaches you basic boxing movement and footwork. This 30-minute session helps you learn simple boxing steps, improve your balance, and practice moving around safely.',
    timedInstructions: [
      // 0:00-3:00 Warm-up and stance setup
      {
        time: 0,
        text: 'Stand tall in Ready. Two tracks under your feet, knees soft, head over hips. Breathe in your nose, out your mouth.',
      },
      {
        time: 12,
        text: 'Boxing stance. If you write with your right hand, put your left foot forward. If not, switch feet.',
      },
      {
        time: 24,
        text: 'Set your feet like train tracks. Front foot points forward. Back foot turns a little out. Heels light. Front foot points forward. Back foot turns a little out. Heels light. Fantastic form!',
      },
      {
        time: 36,
        text: "Alright champion, let's build our perfect fighter's stance from the ground up! First, imagine your feet are on two separate train tracks, not a tightrope. This gives you super balance.",
      },
      {
        time: 44,
        text: "Awesome! Now, bend your knees like you're about to jump, and lean your weight back just a tiny bit onto your back foot. This loads up your secret power!",
      },
      {
        time: 52,
        text: 'Great! Now, pretend a string is pulling the very top of your head up to the ceiling, making your back nice and straight and tall, like a superhero.',
      },
      {
        time: 60,
        text: 'Perfect! Time for the shield! Bring both of your fists right up to your cheeks. Now tuck those elbows in tight to protect your body.',
      },
      {
        time: 68,
        text: 'And the final touch: tuck your chin down a little to keep it safe, but keep your eyes looking forward, always watching like a champion. You look awesome!',
      },
      {
        time: 76,
        text: "Now, let's feel that strong stance. March super tiny in place. Be a ninja with quiet feet, just letting your heels kiss the floor. Breathe in... and out.",
      },
      {
        time: 85,
        text: 'And now... freeze! Hold that perfect stance like a statue for three... two... one... Great job!',
      },
      {
        time: 96,
        text: 'Step names. Step and slide means front foot steps a little, back foot slides and follows.',
      },
      { time: 108, text: 'We will move small to stay safe and clean.' },
      { time: 120, text: 'Reset stance. Two tracks, chin down, hands home.' },
      { time: 132, text: 'Breathe in. Breathe out. Ready to move.' },

      // 3:00-6:00 Forward and back step-slide
      {
        time: 144,
        text: 'Start with Forward step-slide. Front foot steps a little forward. Back foot slides and follows. Keep stance width.',
      },
      {
        time: 156,
        text: 'Go forward one step-slide. Then back to start. Do 4... Remeber, Front foot steps a little forward. Back foot slides and follows. Keep stance width. 4... 3... 2... 1... fantastic!',
      },
      {
        time: 168,
        text: 'Reminder. Front foot first when you go forward. Stay on two tracks.',
      },
      {
        time: 180,
        text: 'Do 4 more forward step-slides. Quiet feet. Front foot steps a little forward. Back foot slides and follows. Keep stance width. Do 4 more... 3... 2... 1... fantastic!',
      },
      {
        time: 192,
        text: 'Now Back step-slide. Back foot steps a little back. Front foot slides and follows.',
      },
      {
        time: 204,
        text: 'Go back one step-slide. Then forward to start. Do 4.',
      },
      {
        time: 216,
        text: 'Reminder. Back foot first when you go back. Guard up.',
      },
      { time: 228, text: 'Do 4 more back step-slides. Breathe steady.' },
      {
        time: 240,
        text: 'Forward 2 steps in a row. Then back 2 steps in a row.',
      },
      { time: 252, text: 'Balance check. Hold guard for 3, 2, 1.' },
      { time: 264, text: 'Reset. Two tracks, knees soft, eyes up.' },
      { time: 276, text: 'Breathe in. Breathe out.' },

      // 6:00-9:00 Side step-slide left and right
      {
        time: 288,
        text: 'Side step-slide next. To go left, lead foot steps left, trail foot slides and follows. Keep stance width.',
      },
      {
        time: 300,
        text: 'Go left one step-slide. Then right back to start. Do 4. Go to the left, lead foot steps left, trail foot slides and follows. Keep stance width.',
      },
      {
        time: 312,
        text: 'Reminder. Lead foot first to go left. Guard stays high.',
      },
      {
        time: 324,
        text: 'Do 4 more left step-slides. Quiet feet. Go to the left, lead foot steps left, trail foot slides and follows. Keep stance width. 4... 3... 2... 1... fantastic!',
      },
      {
        time: 336,
        text: 'To go right, trail foot steps right, lead foot slides and follows.',
      },
      {
        time: 348,
        text: 'Go right one step-slide. Then left back to start. Do 4. 4... 3... 2... 1. Fantastic work!',
      },
      {
        time: 360,
        text: 'Reminder. Trail foot first to go right. Two tracks.',
      },
      {
        time: 372,
        text: 'Do 4 more right step-slides. Knees soft. Go to the right, lead foot steps right, trail foot slides and follows. Keep stance width. 4... 3... 2... 1... fantastic!',
      },
      {
        time: 384,
        text: 'Left, left, right, right. Keep your width. Do 1 set. Left, left, right, right. Keep your width. Fantastic work!',
      },
      { time: 396, text: 'Hold guard for 3, 2, 1. Breathe out.' },
      {
        time: 408,
        text: 'Reset stance. Front foot angle forward. Back foot a little out.',
      },
      { time: 420, text: 'Eyes up. Hands home...next to your cheeks.' },

      // 9:00-12:00 In and out footwork
      {
        time: 432,
        text: 'Next up, In and out. Step in one small forward step-slide. Step out one small back step-slide. Same stance width.',
      },
      {
        time: 444,
        text: 'Do in then out, nice and slow. Do 4. Step in one small forward step-slide. Step out one small back step-slide. Same stance width. 4... 3... 2... 1... fantastic!',
      },
      {
        time: 456,
        text: 'Reminder. Front foot first in, back foot first out.',
      },
      {
        time: 468,
        text: 'Do in and out 4 more times. Step in one small forward step-slide. Step out one small back step-slide. Same stance width. Quiet feet. 4... 3... 2... 1... fantastic!',
      },
      {
        time: 480,
        text: 'Add a tiny freeze. Step in, hold for 1, then step out. Step in one small forward step-slide. Step out one small back step-slide. Same stance width. Again... 4... 3... 2... 1... fantastic!',
      },
      {
        time: 486,
        text: 'One more time, ...but this time, add a tiny freeze. Step in, hold for 1, then step out. Step in one small forward step-slide. Step out one small back step-slide. Same stance width. Again... 4... 3... 2... 1... fantastic!',
      },
      {
        time: 504,
        text: 'Check your posture. Two tracks, knees over toes, head over hips.',
      },
      {
        time: 516,
        text: 'Hold guard, hands next to your cheeks, bounce on your toes, nice and fluid for 3... 2... 1... Fantastic!',
      },

      // 12:00-15:00 Circle steps
      {
        time: 528,
        text: 'Next, Circle steps to the left. Tiny side steps left to make a small circle around your spot. Keep stance.',
      },
      {
        time: 540,
        text: 'Circle left four small steps. 4... 3... 2... 1... Then stop.',
      },
      { time: 552, text: 'Reminder. Feet land soft. Stance stays wide.' },
      {
        time: 564,
        text: 'Circle left four steps again. Guard up. 4... 3... 2... 1... Fantastic!',
      },
      {
        time: 576,
        text: 'Circle right this time. Tiny side steps right to make a small circle.',
      },
      {
        time: 588,
        text: 'Circle right four small steps. Then stop. 4... 3... 2... 1... Fantastic!',
      },
      {
        time: 600,
        text: 'Circle right four steps again. Quiet feet. 4... 3... 2... 1... Fantastic!',
      },
      {
        time: 612,
        text: 'Circle left two steps, then right two steps. Do 2 sets. Left, left, right, right. Left, left, right, right. Fantastic work!',
      },
      { time: 624, text: 'Balance check. Hold guard for 3, 2, 1.' },
      {
        time: 636,
        text: 'Reset stance. Two tracks, knees soft, head over hips. Breathe in. Breathe out.',
      },

      // 15:00-18:00 Diagonal triangle steps
      {
        time: 648,
        text: 'Triangle forward now. Step forward left on a tiny diagonal, slide back foot. Then back to the start. ',
      },
      {
        time: 660,
        text: 'Do forward left diagonal once. Then return. Do 3. Step forward left on a tiny diagonal, slide back foot. Then back to the start. And do it again... now on last time... light on your toes!',
      },
      {
        time: 672,
        text: 'Triangle forward right. Step forward right diagonal, slide back foot. Then return.',
      },
      { time: 684, text: 'Do forward right diagonal once. Then return. Do 3.' },
      {
        time: 696,
        text: 'Triangle back left. Step back left diagonal, slide front foot. Then return.',
      },
      {
        time: 708,
        text: 'Do back left diagonal once. Step back left diagonal, slide front foot. Then return. Again, Step back left diagonal, slide front foot. Then return. One more time, light on your toes.',
      },
      {
        time: 720,
        text: 'Triangle back right. Step back right diagonal, slide front foot. Then return.',
      },
      {
        time: 732,
        text: 'Do back right diagonal again. Step back right diagonal, slide front foot. Then return. Fantastic! One more time, light on your toes.',
      },
      {
        time: 744,
        text: 'Keep stance width the same on every diagonal.',
      },
      {
        time: 756,
        text: 'Hold guard, hands next to your cheeks, bounce on your toes, nice and fluid for 3... 2... 1... Fantastic!',
      },

      // 18:00-21:00 Angle step and L step
      {
        time: 768,
        text: 'Next, we Angle step. Step to the side a little, then step forward a tiny bit to make a new line. Two tracks stay.',
      },
      {
        time: 780,
        text: 'Do angle step left, then return. Do 3. 3... 2... 1... fantastic!',
      },
      {
        time: 792,
        text: 'Do angle step right, then return. Step to the side a little, then step forward a tiny bit to make a new line. Do 3. 3... 2... 1... fantastic!',
      },
      {
        time: 804,
        text: 'Step small. Then slide the other foot to keep stance.',
      },
      {
        time: 816,
        text: 'L step next. Step left, then step back tiny to make an L shape. Guard high.',
      },
      {
        time: 828,
        text: 'Do L step, then return. Do 3. Step left, then step back tiny to make an L shape. Guard high. 3... 2... 1... fantastic!',
      },
      {
        time: 840,
        text: 'L step to the other side. Step right, then step back tiny. Do 3. Step right, then step back tiny. Do 3. 3... 2... 1... fantastic!',
      },
      { time: 852, text: 'Hold guard for 3, 2, 1. Breathe steady.' },

      // 21:00-24:00 Pivots basic
      {
        time: 864,
        text: 'Next, Front foot pivot. Turn on the ball of your front foot a little. Swing the back foot to face a new side. Stance keeps width.',
      },
      {
        time: 876,
        text: "Alright, time to learn a super cool move that lets you change directions like lightning! We're going to practice turning, or pivoting. Let's start by turning to our right.",
      },
      {
        time: 884,
        text: "Get in your normal stance with your left foot forward. Now, to turn right, imagine you're squishing a bug under the ball of your front foot. As you squish the bug and turn, let your back foot swing around with you. Let's try it slowly. Turn and swing! Perfect!",
      },
      {
        time: 896,
        text: "Okay, 20-second drill! Let's do some pivots to the right! Ready? Go! Turn on that front foot, swing the back foot. And reset. Again! Turn and swing! And reset.",
      },
      {
        time: 906,
        text: "Great job! Keep your guard up by your cheeks the whole time. Let's do three more! ONE! TWO! and THREE! Awesome pivots!",
      },
      {
        time: 912,
        text: "Okay champion, let's learn a secret move that unlocks all your power! Remember how we talked about 'squishing the bug' with your back foot when you throw a power Cross?",
      },
      {
        time: 921,
        text: 'That little turn of your back foot is what turns your hips and sends a wave of energy right to your fist! For 20 seconds, we are going to practice just that power-up motion. No punches yet!',
      },
      {
        time: 932,
        text: "Get in your stance, and just practice turning your back foot like you're squishing a bug, feeling your hips turn with it. Ready? Go!",
      },
      {
        time: 940,
        text: "Turn and reset. Feel that? It's like you're loading a giant spring! This is the secret to a knockout punch! Ten seconds left, keep practicing that turn.",
      },
      {
        time: 951,
        text: "Great! Now let's link that power to our footwork. We're going to throw a power Cross, and then immediately pivot on our front foot to face a new direction. It's a 'punch and move' combo!",
      },
      {
        time: 962,
        text: "So the combo is: POWER CROSS, then PIVOT RIGHT! Throw your back-hand Cross with the 'squish the bug' turn... and right after you punch, turn on the ball of your front foot and swing your back leg around. Let's try it!",
      },
      {
        time: 974,
        text: "Punch and move! You just created a whole new angle! This is super advanced footwork, and you're doing great! Let's do it again! POWER CROSS... then PIVOT!",
      },
      {
        time: 985,
        text: "One more time! Punch and pivot! Fantastic! That's how you stay one step ahead of everyone else!",
      },

      // 24:00-27:00 Step ladders and patterns
      {
        time: 990,
        text: "Alright champion, let's put our punches away for a minute and focus on what makes a boxer truly great: amazing footwork! It's time for a footwork challenge! Get in your stance, hands up, and get bouncy!",
      },
      {
        time: 1000,
        text: "Our first drill is the Quick Feet Ladder! For 30 seconds, we're going to shuffle forward and back. Remember to stay on your toes! Ready? Go!",
      },
      {
        time: 1008,
        text: "Take two quick shuffles forward... then two quick shuffles back! That's it! Your front foot always moves first when you go forward, and your back foot moves first when you go back.",
      },
      {
        time: 1018,
        text: "Halfway there! Keep those hands glued to your cheeks! Don't let them drop just because we're moving our feet! Keep your knees bent!",
      },
      {
        time: 1026,
        text: 'Five seconds left! Quick, quick, quick! 5... 4... 3... 2... 1...',
      },
      {
        time: 1032,
        text: "TIME! Great job! Now, we're going side-to-side for 30 seconds! Same rules: stay on your toes, hands up! Ready? Go!",
      },
      {
        time: 1040,
        text: "Two shuffles to the left... and two shuffles to the right! Don't let your feet get all tangled up. Imagine they're on two separate train tracks.",
      },
      {
        time: 1050,
        text: "You're doing great! This is how you stay out of danger and set up your own punches! Ten seconds left!",
      },
      {
        time: 1058,
        text: 'Give me one more trip each way! Left... and right! Fantastic!',
      },
      {
        time: 1064,
        text: "Okay, now for the Box Step Challenge! We are going to draw a big square on the floor with our feet. We'll go forward, then right, then back, then left. Let's try it!",
      },
      {
        time: 1075,
        text: "Ready? Shuffle FORWARD! Now shuffle RIGHT! Good! Now shuffle BACK! And shuffle LEFT to finish the square! Let's do it again!",
      },
      {
        time: 1086,
        text: "Forward... right... back... left! Keep that guard up! You're halfway there, let's do one more big square!",
      },
      {
        time: 1096,
        text: 'Forward... to the right... all the way back... and to the left! Awesome!',
      },
      {
        time: 1104,
        text: "Alright, my favorite drill! It's called Coach Says! It's like Simon Says. You have to listen closely and move fast! Ready?",
      },
      {
        time: 1113,
        text: 'Coach says... shuffle IN! Good! Coach says... shuffle OUT! Coach says... two shuffles LEFT! Great speed!',
      },
      {
        time: 1122,
        text: "Coach says... two shuffles RIGHT! Now... PIVOT! Turn on that front foot like you're squishing a bug! Awesome!",
      },
      {
        time: 1131,
        text: 'Coach says... shuffle OUT! Now for a freeze challenge! Coach says... shuffle IN... and FREEZE! Hold that perfect stance for 3... 2... 1... Great job!',
      },
      {
        time: 1143,
        text: "Now for some Super Combo Footwork! We're going to link moves together. The first combo is: In, Out, Left, Right. Let's do it together slowly.",
      },
      {
        time: 1153,
        text: "Ready? Shuffle IN... shuffle OUT... shuffle LEFT... shuffle RIGHT! Now let's do it faster for 20 seconds! Go!",
      },
      {
        time: 1162,
        text: 'In, out, left, right! Stay light on your feet! Keep those hands home by your cheeks!',
      },
      {
        time: 1170,
        text: 'One more time through the combo! In, out, left, right! You got it!',
      },
      {
        time: 1176,
        text: "Now a new combo! IN, two steps LEFT, then OUT! Ready? Let's go!",
      },
      {
        time: 1184,
        text: "Shuffle forward, circle to the left, then shuffle back out! You're moving like a pro! Let's do it one more time!",
      },
      {
        time: 1192,
        text: 'IN... circle LEFT... and OUT! Fantastic!',
      },
      {
        time: 1197,
        text: "Alright, final drill! It's time for Lightning Feet! This is all about speed! We're going to do tiny, fast steps in place for 15 seconds! Ready? GO, GO, GO!",
      },
      {
        time: 1206,
        text: "Faster! Faster! It's a blur! Keep your hands up! 5... 4... 3... 2... 1...",
      },
      {
        time: 1213,
        text: 'Great! Now quick shuffles, in and out! In, out, in, out! As fast as you can for 15 seconds! Go!',
      },
      {
        time: 1222,
        text: "Stay on those toes! Knees bent! You're almost at the finish line! 5... 4... 3... 2... 1!",
      },
      {
        time: 1229,
        text: 'TIME! AMAZING FOOTWORK, CHAMPION! You moved like a pro today! Take a deep breath! Incredible work!',
      },
    ],
    timeTotal: 1780,
    equipment: 'None',
  },
  // INTERMEDIATE BOXING FOOTWORK (45 minutes) Done
  {
    id: 2094,
    name: 'Intermediate Boxing Footwork',
    categories: ['Footwork', 'intermediate'],
    sports: ['boxing'],
    difficulty: 'intermediate',
    skill: 'Footwork',
    points: 30,
    instructions:
      'Intermediate Boxing Footwork builds on your basic boxing steps with harder moves and better coordination. This 30-minute session helps you practice better boxing movement, fun combinations, and cool patterns.',
    timedInstructions: [
      // 0:00-3:00 Warm up and stance
      {
        time: 0,
        text: 'Stand tall in Ready. Two tracks under your feet, knees soft, head over hips. Breathe in your nose, out your mouth.',
      },
      {
        time: 12,
        text: 'Boxing stance. If you write with your right hand, put your left foot forward. If not, switch feet.',
      },
      {
        time: 24,
        text: 'Feet like train tracks. Front foot points forward. Back foot turns a little out. Heels light.',
      },
      {
        time: 36,
        text: 'Weight split. A little more weight on the back foot. Knees soft.',
      },
      {
        time: 48,
        text: 'Guard up. Fists by cheeks, elbows in, chin down, eyes up.',
      },
      {
        time: 60,
        text: 'Posture check. Two tracks, knees over toes, tall spine, quiet feet.',
      },
      {
        time: 72,
        text: 'March tiny in place. Heels kiss the floor. Breathe smooth.',
      },
      { time: 84, text: 'Stand still. Hold guard for 3, 2, 1.' },
      {
        time: 96,
        text: 'Step and slide means one foot steps a little, the other foot slides and follows.',
      },
      { time: 108, text: 'We move small to stay safe and clean.' },

      // 3:00-6:00 Forward and back step-slide
      {
        time: 120,
        text: 'Forward step-slide definition. Front foot steps forward. Back foot slides and follows. Keep stance width.',
      },
      {
        time: 132,
        text: 'Go forward one step-slide. Then back to start. Do 2.',
      },
      {
        time: 144,
        text: 'Reminder. Front foot first to go forward. Guard up.',
      },
      { time: 156, text: 'Go forward two in a row. Small and quiet.' },
      {
        time: 168,
        text: 'Back step-slide definition. Back foot steps back. Front foot slides and follows.',
      },
      {
        time: 180,
        text: 'Go back one step-slide. Then forward to start. Do 2.',
      },
      { time: 192, text: 'Reminder. Back foot first to go back. Two tracks.' },
      { time: 204, text: 'Go back two in a row. Keep stance width.' },
      { time: 216, text: 'Forward forward, back back. One round.' },
      { time: 228, text: 'Balance check. Hold guard for 3, 2, 1.' },

      // 6:00-9:00 Side step-slide left and right
      {
        time: 240,
        text: 'Side step-slide definition. To go left, lead foot steps left, trail foot slides and follows.',
      },
      { time: 252, text: 'Step left once. Then right back to start. Do 2.' },
      { time: 264, text: 'Reminder. Lead foot first to go left. Guard high.' },
      { time: 276, text: 'Two left in a row. Quiet feet.' },
      {
        time: 288,
        text: 'To go right, trail foot steps right, lead foot slides and follows.',
      },
      { time: 300, text: 'Step right once. Then left back to start. Do 2.' },
      {
        time: 312,
        text: 'Reminder. Trail foot first to go right. Two tracks.',
      },
      { time: 324, text: 'Two right in a row. Knees soft.' },
      { time: 336, text: 'Left, left, right, right. One set.' },
      { time: 348, text: 'Hold guard for 3, 2, 1.' },

      // 9:00-12:00 In and out control
      {
        time: 360,
        text: 'In and out definition. Step in one forward step-slide. Step out one back step-slide. Same width.',
      },
      { time: 372, text: 'In then out slow. Do 3.' },
      {
        time: 384,
        text: 'Reminder. Front foot first in, back foot first out.',
      },
      { time: 396, text: 'In and out again. Quiet feet.' },
      { time: 408, text: 'In in, out out. Small and clean.' },
      { time: 420, text: 'Add freeze. Step in, hold for 1, then step out.' },
      {
        time: 432,
        text: 'Posture. Two tracks, knees over toes, head over hips.',
      },
      { time: 444, text: 'Hold guard for 3, 2, 1.' },

      // 12:00-15:00 Circle steps
      {
        time: 456,
        text: 'Circle left definition. Tiny left steps to draw a small circle around your spot. Keep stance width.',
      },
      { time: 468, text: 'Circle left four tiny steps. Then stop.' },
      { time: 480, text: 'Reminder. Feet land soft. Guard up.' },
      { time: 492, text: 'Circle left again four steps. Breathe steady.' },
      {
        time: 504,
        text: 'Circle right definition. Tiny right steps to draw a small circle.',
      },
      { time: 516, text: 'Circle right four tiny steps. Then stop.' },
      { time: 528, text: 'Circle right again four steps. Quiet feet.' },
      { time: 540, text: 'Left two steps, then right two steps. Do 2 sets.' },
      { time: 552, text: 'Hold guard for 3, 2, 1.' },

      // 15:00-18:00 Diagonal V steps
      {
        time: 564,
        text: 'V step forward definition. Step forward left on a small diagonal, slide the back foot. Then back to start.',
      },
      { time: 576, text: 'Forward left diagonal once. Return. Do 2.' },
      {
        time: 588,
        text: 'V step forward right. Step forward right diagonal, slide the back foot. Return.',
      },
      { time: 600, text: 'Forward right diagonal once. Return. Do 2.' },
      {
        time: 612,
        text: 'V step back left definition. Step back left diagonal, slide the front foot. Return.',
      },
      { time: 624, text: 'Back left diagonal once. Return. Do 2.' },
      {
        time: 636,
        text: 'V step back right. Step back right diagonal, slide the front foot. Return.',
      },
      { time: 648, text: 'Back right diagonal once. Return. Do 2.' },
      {
        time: 660,
        text: 'Reminder. Keep stance width the same on every diagonal.',
      },
      { time: 672, text: 'Hold guard for 3, 2, 1.' },

      // 18:00-21:00 Angle step and L step
      {
        time: 684,
        text: 'Angle step definition. Step to the side a little, then step forward a tiny bit to make a new line. Two tracks stay.',
      },
      { time: 696, text: 'Angle left then return. Do 2.' },
      { time: 708, text: 'Angle right then return. Do 2.' },
      {
        time: 720,
        text: 'Reminder. Step small. Slide the other foot to keep stance.',
      },
      {
        time: 732,
        text: 'L step definition. Step left, then step back tiny to make an L shape. Guard high.',
      },
      { time: 744, text: 'Do L step left then return. Do 2.' },
      { time: 756, text: 'L step right then return. Do 2.' },
      { time: 768, text: 'Hold guard for 3, 2, 1. Breathe steady.' },

      // 21:00-24:00 Pivots
      {
        time: 780,
        text: 'Front foot pivot definition. Turn on the ball of your front foot a little. Swing the back foot to face a new side.',
      },
      { time: 792, text: 'Pivot a little to the right. Then return. Do 2.' },
      { time: 804, text: 'Pivot a little to the left. Then return. Do 2.' },
      { time: 816, text: 'Reminder. Heels light. Knees soft. Turn small.' },
      {
        time: 828,
        text: 'Back foot helper. Turn the back heel a little to help hips face straight.',
      },
      { time: 840, text: 'Tiny back heel turn in place. Then relax. Do 2.' },
      { time: 852, text: 'Link. Angle left, tiny front pivot, return.' },
      { time: 864, text: 'Link. Angle right, tiny front pivot, return.' },

      // 24:00-27:00 Shuffle step and pendulum step
      {
        time: 876,
        text: 'Shuffle step definition. A quick step and slide that covers a little more space. Same stance width.',
      },
      { time: 888, text: 'Shuffle forward one time. Then back to start.' },
      { time: 900, text: 'Shuffle back one time. Then forward to start.' },
      { time: 912, text: 'Reminder. Keep two tracks. Guard up.' },
      {
        time: 924,
        text: 'Pendulum step definition. Little bounce that shifts weight from front foot to back foot without losing stance.',
      },
      {
        time: 936,
        text: 'Pendulum in. Small bounce forward. Then back to start.',
      },
      { time: 948, text: 'Pendulum out. Small bounce back. Then to start.' },
      { time: 960, text: 'Pendulum left and right tiny. Keep heels light.' },

      // 27:00-30:00 Drop step and switch step
      {
        time: 972,
        text: 'Drop step definition. A quick small back step to make space, then return to stance.',
      },
      { time: 984, text: 'Do drop step back once. Then return.' },
      { time: 996, text: 'Do drop step back again. Quiet feet.' },
      {
        time: 1008,
        text: 'Reminder. Back foot first when you go back. Guard high.',
      },
      {
        time: 1020,
        text: 'Switch step definition. A quick feet switch that keeps stance width and puts the other foot forward for a moment.',
      },
      {
        time: 1032,
        text: 'Do one quick switch step. Then switch back to normal stance.',
      },
      { time: 1044, text: 'Do one more quick switch step. Then switch back.' },
      { time: 1056, text: 'Balance check. Hold guard for 3, 2, 1.' },

      // 30:00-33:00 Chains with holds
      {
        time: 1068,
        text: 'Chain. In, out, left, right. Small steps. One round.',
      },
      { time: 1080, text: 'Chain. In, in, out, out. One round.' },
      { time: 1092, text: 'Chain. Angle left, pivot small, return.' },
      { time: 1104, text: 'Chain. Angle right, pivot small, return.' },
      { time: 1116, text: 'Reminder. Two tracks, knees soft, head over hips.' },
      { time: 1128, text: 'Hold guard still for 3, 2, 1.' },
      { time: 1140, text: 'Reset. Shake legs. Hands home.' },
      { time: 1152, text: 'Breathe in. Breathe out.' },

      // 33:00-36:00 Reaction footwork calls
      { time: 1164, text: 'Coach call. In. Step in once.' },
      { time: 1176, text: 'Out. Step out once.' },
      { time: 1188, text: 'Left. Step-slide left once.' },
      { time: 1200, text: 'Right. Step-slide right once.' },
      { time: 1212, text: 'In in. Two small steps in.' },
      { time: 1224, text: 'Out out. Two small steps out.' },
      { time: 1236, text: 'Circle left. Two tiny steps.' },
      { time: 1248, text: 'Circle right. Two tiny steps.' },
      { time: 1260, text: 'Angle left. One small angle.' },
      { time: 1272, text: 'Angle right. One small angle.' },
      { time: 1284, text: 'Pivot. Small front foot pivot.' },
      { time: 1296, text: 'Reset stance. Two tracks, chin down, hands home.' },

      // 36:00-39:00 Speed and rhythm
      {
        time: 1308,
        text: 'Quick feet. Tiny fast steps in place for 4 counts. Then freeze guard.',
      },
      { time: 1320, text: 'In and out quick. In, out, in, out. Then guard.' },
      {
        time: 1332,
        text: 'Left and right quick. Left, right, left, right. Then guard.',
      },
      { time: 1344, text: 'Circle quick. Two left, two right. Then guard.' },
      { time: 1356, text: 'Reminder. Breathe soft. Quiet feet. Hands home.' },
      {
        time: 1368,
        text: 'Posture cue. Tall spine, knees soft, head over hips.',
      },
      { time: 1380, text: 'Balance test. Stand still in guard for 3, 2, 1.' },
      { time: 1392, text: 'Shake hands. Shake legs. Guard up.' },

      // 39:00-42:00 Mixed patterns
      {
        time: 1404,
        text: 'Pattern. Box pattern. Forward, right, back, left. One round.',
      },
      {
        time: 1416,
        text: 'Pattern. Zigzag. Forward left, forward right, back right, back left. One round.',
      },
      { time: 1428, text: 'Pattern. In, left, out, right. One round.' },
      {
        time: 1440,
        text: 'Pattern. Angle left, angle right, pivot small. One round.',
      },
      {
        time: 1452,
        text: 'Reminder. Keep stance width the same on every move.',
      },
      { time: 1464, text: 'Hold guard still for 3, 2, 1.' },
      { time: 1476, text: 'Breathe in. Breathe out.' },
      { time: 1488, text: 'Proud posture. Eyes up.' },

      // 42:00-45:00 Cooldown and finish
      { time: 1500, text: 'Slow sway side to side. Breathe out.' },
      {
        time: 1512,
        text: 'Neck. Ear to shoulder right, then left. Gentle and small.',
      },
      {
        time: 1524,
        text: 'Shoulders roll. Two forward, two back. Calm breath.',
      },
      {
        time: 1536,
        text: 'Hamstrings. Hinge to knees, hold for 3, 2, 1, rise slow.',
      },
      { time: 1548, text: 'Ankles roll. Right two, left two. Quiet feet.' },
      { time: 1560, text: 'Posture scan. Two tracks, soft knees, tall spine.' },
      { time: 1572, text: 'Deep breath in. Long breath out.' },
      {
        time: 1584,
        text: 'Coach kudos. Clean steps, quiet feet, strong guard.',
      },
      { time: 1596, text: 'Stand tall. Hands by sides. Smile.' },
      { time: 1608, text: 'Hold still for 3, 2, 1.' },

      // Gentle breath cues to the 45:00 mark
      { time: 1620, text: 'Calm breath in.' },
      { time: 1632, text: 'Calm breath out.' },
      { time: 1644, text: 'Relax hands.' },
      { time: 1656, text: 'Relax shoulders.' },
      { time: 1668, text: 'Relax face.' },
      { time: 1680, text: 'Soft knees.' },
      { time: 1692, text: 'Head over hips.' },
      { time: 1704, text: 'Arms by sides.' },
      { time: 1716, text: 'Gentle inhale.' },
      { time: 1728, text: 'Gentle exhale.' },
      { time: 1740, text: 'Small smile.' },
      { time: 1752, text: 'You did great.' },
      { time: 1764, text: 'Hold still for 3, 2, 1.' },
      { time: 1776, text: 'Shake out hands.' },
      { time: 1788, text: 'Shake out legs.' },
      { time: 1800, text: 'Stand tall again.' },
      { time: 1812, text: 'Breath in.' },
      { time: 1824, text: 'Breath out.' },
      { time: 1836, text: 'Calm heart.' },
      { time: 1848, text: 'Calm body.' },
      { time: 1860, text: 'Ready to hydrate.' },
      { time: 1872, text: 'Grab water if you need.' },
      { time: 1884, text: 'Stand relaxed.' },
      { time: 1896, text: 'Soft breath.' },
      {
        time: 1908,
        text: 'Proud work today. High five. Session complete. Intermediate Boxing Footwork finished.',
      },
    ],
    timeTotal: 1912,
    equipment: 'None',
  },
  // ADVANCED BOXING FOOTWORK (45 minutes) Done
  {
    id: 2095,
    name: 'Advanced Boxing Footwork',
    categories: ['Footwork', 'advanced'],
    sports: ['boxing'],
    difficulty: 'advanced',
    skill: 'Footwork',
    points: 30,
    instructions:
      'Advanced Boxing Footwork teaches you fancy boxing movement and tricky coordination. This 30-minute session helps you practice cool boxing steps, fun combinations, and expert coordination exercises.',
    timedInstructions: [
      {
        time: 0,
        text: "Alright Champion, let's get to work. Today is all about elite-level footwork. We're going to move with speed, precision, and purpose. Get in your stance, guard up. Let's begin the warm-up.",
      },
      {
        time: 10,
        text: "Start with a light boxer's bounce. Stay on the balls of your feet. We're here for 30 seconds, just finding our rhythm. Keep your hands connected to your cheeks.",
      },
      {
        time: 25,
        text: "Good. Now, start circling to your left, using small shuffle steps. Keep your feet apart, stay balanced. Imagine you're circling the outside of a ring.",
      },
      {
        time: 40,
        text: "And now, circle to your right. Small, disciplined steps. Don't let your feet cross. This is all about control.",
      },
      {
        time: 55,
        text: "Back to center. Let's do some dynamic stretching. Gentle leg swings, forward and back. Let's do five on the right leg. One... two... three... four... five.",
      },
      {
        time: 70,
        text: 'And now the left leg. One... two... three... four... five. Good. Now hands on your hips, big torso twists to warm up that core.',
      },
      {
        time: 85,
        text: "Now, for the rest of the warm-up, we're doing pattern shadow boxing. I call out a footwork pattern, you do it while throwing light punches. Ready? Let's do the Box Step. Forward, right, back, left. Go!",
      },
      {
        time: 100,
        text: 'Keep moving through that square pattern. Throw your jab and cross. Stay light on your feet. Keep your guard high.',
      },
      {
        time: 115,
        text: "Now, let's reverse the box. Forward, left, back, right. Keep the punches flowing. This is about coordinating your hands and feet.",
      },
      {
        time: 130,
        text: 'Now, just forward and back shuffles. Two forward, two back. As you step forward, throw your jab. As you step back, stay defensive.',
      },
      {
        time: 145,
        text: 'Ten more seconds! Pick up the pace! Fast feet, fast hands! 5... 4... 3... 2... 1...',
      },
      {
        time: 155,
        text: 'TIME! Great warm-up. Grab a quick sip of water. Our first round starts in one minute. Today is all about three-minute rounds, just like the pros.',
      },
      {
        time: 185,
        text: "Listen up. This first round is all about creating angles. We're going to master the Triangle Step. For the first minute, we're only stepping to our lead side. Get ready.",
      },
      {
        time: 215,
        text: "Round One! Three minutes of work! Let's go! Begin with the Triangle Step to your lead side. Step out with your front foot, let the back foot follow. Go!",
      },
      {
        time: 225,
        text: "Step-slide. And reset. Again. Step-slide. And reset. You're cutting the corner, creating a new line of attack. Keep your guard up.",
      },
      {
        time: 240,
        text: 'The goal is to be explosive. A small, sharp step. Keep drilling it. Step-slide. Good. Thirty seconds down on this side.',
      },
      {
        time: 255,
        text: "Ten more seconds on this side. Let's see five perfect ones. ONE! TWO! THREE! FOUR! FIVE!",
      },
      {
        time: 275,
        text: "Okay, minute two. We're going the other way. Triangle Step to your rear side. This feels different, so focus. Step out with your rear foot first, then the front foot follows. Go!",
      },
      {
        time: 288,
        text: "This is a defensive angle, getting you away from their power hand. Step with the back, slide with the front. It takes coordination, you've got this.",
      },
      {
        time: 303,
        text: "Halfway through this minute. Keep your balance. Don't let your feet get too wide. Stay in that strong stance.",
      },
      {
        time: 318,
        text: "Ten more seconds on this side. Let's get five sharp ones. ONE! TWO! THREE! FOUR! FIVE!",
      },
      {
        time: 335,
        text: "Final minute of the round! We're alternating. I call it, you do it. Ready? ANGLE LEFT! Good. Now, ANGLE RIGHT!",
      },
      {
        time: 350,
        text: 'Keep it sharp. ANGLE LEFT! ANGLE RIGHT! Keep your hands high! This is how you control the ring.',
      },
      {
        time: 365,
        text: 'Thirty seconds left! Pick up the pace! Left! Right! Left! Right! Stay balanced!',
      },
      {
        time: 380,
        text: 'Final ten seconds! Give me your fastest angles! 5... 4... 3... 2... 1...',
      },
      {
        time: 395,
        text: 'TIME! Round one is done. Great work. One minute of active rest. Shake out your legs, take deep breaths. Stay on your feet.',
      },
      {
        time: 425,
        text: "Listen up. Round Two is about combining footwork with defense. We're going to link our shuffles and pivots with head movement. No punching this round, just pure movement.",
      },
      {
        time: 455,
        text: "Round Two! Let's go! First minute, we're linking the shuffle back with a slip. The combo is: Shuffle back, then slip to your rear side. Go!",
      },
      {
        time: 468,
        text: 'Create space, then evade. Back and slip. Back and slip. Imagine a jab flying past your head as you slip.',
      },
      {
        time: 483,
        text: "Keep the movement sharp. Don't be lazy. A small step back, a sharp slip. Keep your hands glued to your cheeks.",
      },
      {
        time: 498,
        text: "Ten seconds on this combo. Let's see five sharp ones. Go!",
      },
      {
        time: 515,
        text: "Minute two. Now we're linking the pivot with a roll. The combo is: Pivot to your lead side, then roll under. Go!",
      },
      {
        time: 528,
        text: 'Change your angle with the pivot, then get underneath their hook with the roll. Pivot and roll. Pivot and roll.',
      },
      {
        time: 543,
        text: "Stay low when you roll. Bend at the knees and draw a 'U' with your head. Keep those legs strong.",
      },
      {
        time: 558,
        text: 'Ten seconds on this combo. Give me five perfect ones! Go!',
      },
      {
        time: 575,
        text: "Final minute of the round! I'm calling them out, you react. Ready? SHUFFLE AND SLIP! Good. PIVOT AND ROLL!",
      },
      {
        time: 590,
        text: 'Again! SHUFFLE AND SLIP! PIVOT AND ROLL! You are impossible to hit! Keep that intensity!',
      },
      {
        time: 605,
        text: 'Thirty seconds left! Be sharp! Be focused! SHUFFLE AND SLIP! PIVOT AND ROLL!',
      },
      {
        time: 620,
        text: 'Final ten seconds! Your fastest ones yet! Go, go, go! 5... 4... 3... 2... 1...',
      },
      {
        time: 635,
        text: 'TIME! Excellent movement. One minute rest. Grab a sip of water. We have one more technique round before the championship round.',
      },
      {
        time: 665,
        text: "Listen up. Round Three is an advanced coordination drill. We're going to practice switching stances, but we're going to punch from both sides. This will challenge your brain and your balance.",
      },
      {
        time: 695,
        text: "Round Three! Let's go! Start in your normal stance. Give me a perfect one-two combo. Jab-Cross! Good. Now, get ready... SWITCH STANCES!",
      },
      {
        time: 710,
        text: 'Jump and switch your feet. Find your balance. Now, from this new stance, give me a one-two! New front hand, new back hand! Go!',
      },
      {
        time: 725,
        text: "Good! Now, SWITCH BACK to your normal stance! Perfect. Let's repeat. Normal one-two! SWITCH! Opposite one-two!",
      },
      {
        time: 740,
        text: "That's one minute down, two to go. Keep this rotation. The goal is to be balanced and powerful from both sides. SWITCH BACK!",
      },
      {
        time: 755,
        text: "Normal Jab-Cross! SWITCH! Opposite Jab-Cross! Your brain is working hard right now, that's good!",
      },
      {
        time: 770,
        text: "Don't get sloppy with your punches just because your feet are different. Every punch is sharp. SWITCH BACK!",
      },
      {
        time: 785,
        text: "Final minute of the round! Let's pick up the pace a little. Normal one-two! SWITCH! Opposite one-two! SWITCH BACK!",
      },
      {
        time: 800,
        text: "Thirty seconds left! Stay focused! Keep your guard up on every switch! Don't get lazy!",
      },
      {
        time: 815,
        text: 'Final ten seconds! One more rotation! Normal one-two! SWITCH! Opposite one-two! SWITCH BACK!',
      },
      {
        time: 830,
        text: "TIME! That was a tough one. Great job. One minute rest. This is your last rest. After this, it's the Championship Round.",
      },
      {
        time: 860,
        text: "Listen close. The Championship Round. Three minutes. I'm going to call out complex combinations of everything we've worked on. You perform them with speed and precision. This is the final test.",
      },
      {
        time: 890,
        text: "Round Four! The Championship Round! Let's GO! First combo is: Triangle Step left, throw a one-two, then roll out! Go!",
      },
      {
        time: 905,
        text: "Angle, punch, defend. That's the sequence. Let's drill it. Angle left, one-two, roll! Again! Angle left, one-two, roll!",
      },
      {
        time: 920,
        text: "First minute is almost down. Keep that combo sharp. Don't rush it, make every move perfect.",
      },
      {
        time: 935,
        text: 'Okay, new combo for the second minute! Shuffle back, slip, fire a power Cross, then pivot right! Go!',
      },
      {
        time: 950,
        text: 'Defend, punch, change your angle. This is high-level stuff. Back, slip, Cross, pivot! Again! Back, slip, Cross, pivot!',
      },
      {
        time: 965,
        text: 'Stay focused! This is where fatigue sets in. Keep your form clean. Thirty seconds left on this combo.',
      },
      {
        time: 980,
        text: "Okay, final minute of the entire workout! You're going to alternate between those two big combos! Ready? First one! ANGLE, ONE-TWO, ROLL!",
      },
      {
        time: 995,
        text: 'Good! Now the second one! BACK, SLIP, CROSS, PIVOT!',
      },
      {
        time: 1010,
        text: "Thirty seconds left! Give it everything you've got! ANGLE, ONE-TWO, ROLL! And... BACK, SLIP, CROSS, PIVOT!",
      },
      {
        time: 1025,
        text: 'Final ten seconds! One more of each! Go! 5... 4... 3... 2... 1...',
      },
      {
        time: 1040,
        text: "TIME! YOU DID IT! That was an elite-level workout! I am seriously impressed. Let's cool down.",
      },
      {
        time: 1050,
        text: 'Just walk around slowly for a moment. Take deep, slow breaths. In through your nose, out through your mouth. Let that heart rate come down.',
      },
      {
        time: 1065,
        text: "Okay, let's stretch those legs. First, a calf stretch. Put one foot behind you, leg straight, and press your heel to the floor. Hold it.",
      },
      {
        time: 1080,
        text: 'And switch sides. Other foot back, press the heel down. You earned this stretch.',
      },
      {
        time: 1095,
        text: 'Now for the front of your legs. Hold onto something if you need to. Grab one foot and gently pull your heel towards your seat.',
      },
      {
        time: 1110,
        text: 'And switch sides. Grab the other foot and hold. Great balance.',
      },
      {
        time: 1125,
        text: "Now, feet wide apart, let's do a butterfly stretch. Bend your knees and put your hands on your inner thighs, gently pushing out to stretch your groin.",
      },
      {
        time: 1140,
        text: "And slowly come up. Let's stretch your back. Clasp your hands in front of you and round your upper back, pushing your hands away.",
      },
      {
        time: 1155,
        text: 'Now, clasp your hands behind your back and push your chest out to stretch your shoulders and chest.',
      },
      {
        time: 1170,
        text: "Excellent work today, Champion. That was a true advanced workout. You should be incredibly proud. Drink plenty of water. You've earned it.",
      },
    ],
    timeTotal: 1685,
    equipment: 'None',
  },
  // BEGINNER BOXING DEFENSE (30 minutes) Done
  {
    id: 2096,
    name: 'Beginner Boxing Defense',
    categories: ['Defense', 'beginner'],
    sports: ['boxing'],
    difficulty: 'beginner',
    skill: 'Defense',
    points: 22,
    instructions:
      'Beginner Boxing Defense teaches you basic defensive movements and blocks. This 22-minute session helps you learn simple defensive moves, improve your protection, and practice blocking safely.',
    timedInstructions: [
      // 0:00-3:00 Warm up and stance
      {
        time: 0,
        text: "Alright champion, let's begin by building our perfect boxer's stance from the ground up. Start by just standing tall and relaxed. Take a deep breath in... and out.",
      },
      {
        time: 9,
        text: "Now, let's figure out which foot goes forward. It's easy! If you write or throw a ball with your right hand, I want you to take a big, comfortable step forward with your LEFT foot.",
      },
      {
        time: 19,
        text: "If you're a lefty, just do the opposite: take a step forward with your RIGHT foot. This is your strong, balanced starting position.",
      },
      {
        time: 28,
        text: "Perfect! Now look down at your feet. We want them about as wide as your shoulders, like you're standing on two separate train tracks, not a skinny tightrope. This gives you super balance!",
      },
      {
        time: 39,
        text: "Great! Your front foot should point mostly forward. Now turn your back foot out a little bit, like it's pointing to the corner. This helps you turn your body for those power punches later.",
      },
      {
        time: 50,
        text: "Awesome! Now, let's get athletic. Bend both your knees like you're a spring, ready to jump. And lean just a tiny bit of your weight onto your back foot. You should feel powerful.",
      },
      {
        time: 61,
        text: "The foundation is set! Time for the shield! Make two soft fists and bring them right up to your cheeks. Don't squeeze them tight! And tuck your elbows in close to your body to protect your ribs.",
      },
      {
        time: 73,
        text: "And the last, most important part: tuck your chin down a little bit, like you're holding a secret message under it. But keep your eyes looking straight ahead. Always see what's in front of you.",
      },
      {
        time: 84,
        text: "This is it! This is your boxer's stance! Let's feel it. Start a tiny, light bounce in place. Be a ninja with quiet feet, just letting your heels kiss the floor. Breathe in... and out.",
      },
      {
        time: 96,
        text: 'And now... FREEZE! Hold that perfect, strong stance like a superhero statue. Feel your balance from your feet all the way to your head. For three... two... one...',
      },
      {
        time: 108,
        text: 'Excellent! That is the perfect stance. It keeps you safe, balanced, and ready to move. Remember this feeling!',
      },

      {
        time: 120,
        text: "Alright champion, let's work on our defense! We're going to build an unbreakable superhero shield called the High Guard. It's super strong and keeps you safe!",
      },
      {
        time: 128,
        text: 'Get in your stance, and bring both fists right up to your cheeks. Now, squeeze your elbows in tight so they touch your ribs, and tuck your chin down. Perfect!',
      },
      {
        time: 136,
        text: "Now, hold it! Feel how strong and solid that is? Imagine you're a fortress and nothing can get through! Hold it strong... breathe... for 5... 4... 3... 2... 1.",
      },
      {
        time: 146,
        text: "Awesome! Now, from inside our fortress, we're going to learn a cool move to stop punches. It's called the Catch Block, and it's like catching a fast baseball right in front of your face!",
      },
      {
        time: 156,
        text: "Using your front hand, you're going to make a tiny, quick push forward with your palm to 'catch' the imaginary punch, then snap your hand right back to your cheek. It's a small, fast move.",
      },
      {
        time: 166,
        text: "Let's practice! We're going to do four of them, and I'll count! Keep your feet quiet! Ready? ONE! Catch and back! TWO! Catch and back! THREE! Super fast! FOUR! Great job!",
      },
      {
        time: 177,
        text: "Excellent! Your hand always starts at your cheek, catches the punch, and returns home to your cheek. Let's do four more, even sharper this time! Ready?",
      },
      {
        time: 185,
        text: 'ONE! Chin is always tucked! TWO! Elbows are in! THREE! Perfect! and FOUR! Fantastic catch blocks!',
      },

      {
        time: 192,
        text: "Okay defensive superstar, you've learned how to block. Now let's learn a quicker move called the Parry. It's like a tiny, fast karate chop that knocks a punch safely off course!",
      },
      {
        time: 202,
        text: "Using your front hand, you're going to make a small, quick tap outwards to push an imaginary punch away from your face. Your elbow stays in tight. Let's do four of them together. Ready?",
      },
      {
        time: 213,
        text: "ONE! Tap and right back to your cheek! TWO! It's a small, quick move! THREE! That's it! FOUR! Perfect! Let's do four more, even faster! ONE, TWO, THREE, FOUR!",
      },
      {
        time: 225,
        text: "Excellent parries! Now, let's learn how to 'cover up'. Think of your body as a house with two windows we need to shut: the top window is your head, and the bottom window is your body.",
      },
      {
        time: 237,
        text: "First, let's protect the top window with a High Cover. To block a punch to your head, you just bring your glove up and touch your temple, keeping your elbow pointed down. Let's try it on the right side. COVER!",
      },
      {
        time: 248,
        text: "Good! And now the left side. COVER! And back. Let's do two more each side, super fast! Right! Left! Right! Left! You've shut the top windows!",
      },
      {
        time: 259,
        text: "Awesome! Now for the bottom window. This is the Body Cover. To block a punch to your body, you just squeeze your elbow down tight against your ribs. Let's try it. Right side, COVER!",
      },
      {
        time: 270,
        text: "Perfect! And left side, COVER! Let's do two more each side, covering those ribs! Right! Left! Right! Left! Your fortress is secure!",
      },
      {
        time: 281,
        text: 'Fantastic! You can parry and you can cover! Now for our final defensive move for today: the Slip. This is how you make punches miss completely, just like in the movies!',
      },
      {
        time: 292,
        text: "It's all about moving your head off the center line. We're going to do a continuous slip drill. Bend your knees, keep your guard high, and get ready to find a rhythm. Let's go!",
      },
      {
        time: 302,
        text: 'Slip to the right... now slip to the left. Just move your head enough for a punch to fly right past your ear. Keep your eyes looking forward the whole time.',
      },
      {
        time: 313,
        text: "Let's find a rhythm. Right... left... right... left. You're a moving target! It's almost impossible to hit you when you're moving your head like this!",
      },
      {
        time: 324,
        text: 'Ten more seconds of slipping! Stay balanced on your feet, knees soft! 5... 4... 3... 2... 1... And FREEZE! Perfect stance. Awesome defensive work!',
      },

      {
        time: 336,
        text: "Alright champ, let's learn how to change levels like a superhero! First up is the Duck. This is how you get underneath a swinging punch.",
      },
      {
        time: 345,
        text: "The secret is to always bend your knees, NOT your back. Keep your back straight and your eyes looking forward. Let's try it slowly. Just DUCK down by bending your knees... and RISE back up tall.",
      },
      {
        time: 356,
        text: "Let's do that again. DUCK... and RISE. Now, let's try to hold it. DUCK down and hold like a loaded spring for 3... 2... 1... and explode back up! Feel that power in your legs?",
      },
      {
        time: 368,
        text: "Great! Now let's do a drill. We're going to do four ducks in a row, finding a rhythm. Ready? ONE! Down and up! TWO! Keep your back straight! THREE! Hands are high! FOUR! Perfect!",
      },
      {
        time: 380,
        text: "Let's do another set, and this time, we freeze our guard at the top. DUCK... and FREEZE! Good. DUCK... and FREEZE! Two more! DUCK... FREEZE! Last one! DUCK... and FREEZE!",
      },
      {
        time: 394,
        text: "Fantastic ducks! Now that you can move up and down, let's learn how to roll side to side. The Roll is one of the coolest and most important moves in boxing.",
      },
      {
        time: 405,
        text: "Imagine you're drawing a big letter 'U' with your head. You're going to use your legs to bend down, go under an imaginary punch, and come up on the other side. Let's try it.",
      },
      {
        time: 416,
        text: "Let's roll to the right first. Bend your knees, go down and under... and come up on the right side! Good! Now roll to the left. Down and under... and up on the left! You're making punches miss completely.",
      },
      {
        time: 428,
        text: "Okay, let's drill it for 30 seconds. We're going to roll right, then left, right, then left. Find a smooth rhythm, like you're rolling under waves. Ready? Go!",
      },
      {
        time: 439,
        text: 'Roll right... roll left. Keep your hands glued to your cheeks the whole time. The power and movement come from your hips and your knees, not from bending your back.',
      },
      {
        time: 450,
        text: "You're doing great! Keep that U-shape smooth! This is amazing for your defense! Let's do four more! Right... and left... one more each way! Right... and left!",
      },
      {
        time: 462,
        text: 'And FREEZE! Get back in that perfect stance. Two feet on train tracks, chin down, elbows in. Awesome work on your head movement!',
      },
      {
        time: 480,
        text: "Alright champion, let's work on the footwork that makes you untouchable! First, we'll master the Step Back. This is your escape button to create space instantly.",
      },
      {
        time: 489,
        text: "Here's the secret: to move back, your back foot always moves first. Take a small step back with your back foot, then your front foot immediately slides to follow, putting you right back in your strong stance.",
      },
      {
        time: 500,
        text: "Let's drill it for 20 seconds! Step back... then shuffle right back to where you started. Ready? Go! Back foot, then front foot. You're gliding out of danger and getting ready to counter!",
      },
      {
        time: 510,
        text: "Keep your hands high and your chin tucked even when you're moving backward! Let's do three more! ONE! TWO! THREE! Perfect!",
      },
      {
        time: 518,
        text: "Excellent! Now, let's learn how to use the side door! This is the Side Step, for moving left and right with lightning speed.",
      },
      {
        time: 527,
        text: "It's simple: whichever way you want to go, that foot moves first. To go left, your left foot steps, then your right foot follows. Let's try it. Step LEFT... and back to the middle.",
      },
      {
        time: 538,
        text: "Now to the right! Your right foot moves first, then your left foot follows. Step RIGHT... and back to the middle. You're always keeping your feet on those separate train tracks!",
      },
      {
        time: 549,
        text: "Okay, let's drill it! For 20 seconds, we're going left, then right! Ready? Go! Step LEFT... reset! Step RIGHT... reset! Keep that guard high and tight to your cheeks!",
      },
      {
        time: 560,
        text: "Awesome side steps! Now let's revisit our pro-level move: cutting the corner with the Angle Step, also known as the Triangle Step!",
      },
      {
        time: 570,
        text: "Remember, you step out at an angle with your front foot, then your back foot follows to create a new line of attack. Let's try one. Angle step LEFT... and back to center.",
      },
      {
        time: 581,
        text: "Now, Angle step RIGHT... and back. This is how you get around your opponent's guard. Let's drill it for 20 seconds! Left, then right! Keep it sharp!",
      },
      {
        time: 593,
        text: "Fantastic angles! Finally, let's practice the fastest way to turn: the Pivot. This is your secret spin move.",
      },
      {
        time: 602,
        text: "Remember, you turn on the ball of your front foot, like you're squishing a bug, and your back foot swings around. Let's practice pivoting to the right for 15 seconds! Go!",
      },
      {
        time: 612,
        text: "Keep your heels light and your knees soft! Stay balanced! 5... 4... 3... 2... 1... Now let's go the other way! Pivot to the left for 15 seconds! Go!",
      },
      {
        time: 623,
        text: "Great pivots! Keep that guard up! You're turning like a pro! 5... 4... 3... 2... 1... And FREEZE! Back to your perfect stance. Knees soft, head over your hips. Amazing footwork!",
      },

      {
        time: 624,
        text: "Alright defensive master, it's time to sharpen our tools! We're going to drill all our defensive moves, back-to-back. First up, the Catch Block. Let's do a quick set. Ready?",
      },
      {
        time: 633,
        text: "Remember, it's a quick push out and back with your front hand, like catching a baseball! Let's do four! ONE! TWO! THREE! FOUR! Back to guard!",
      },
      {
        time: 643,
        text: "Great! Now the Parry! The little karate chop with your front hand to knock a punch away. Let's do four! ONE! TWO! THREE! FOUR! Perfect!",
      },
      {
        time: 654,
        text: "Awesome! Now let's practice our slips, dodging those laser beams. We're going right, then left. Let's do four sets! RIGHT, LEFT! That's one! RIGHT, LEFT! That's two! Two more!",
      },
      {
        time: 666,
        text: "Now the Duck! Bend those knees, keep your back straight. Let's do four fast ones! DUCK! DUCK! DUCK! DUCK! Excellent!",
      },
      {
        time: 675,
        text: "And finally, the Roll! Draw that 'U' with your head. Let's go right, then left. Four sets! RIGHT, LEFT! That's one! RIGHT, LEFT! Two more! Keep those hands up!",
      },
      {
        time: 688,
        text: "Amazing! You've sharpened all your tools! Now, get ready for the Reaction Game! I'm going to call out a move, you do it as fast as you can. No thinking, just reacting! Listen closely!",
      },
      {
        time: 700,
        text: 'Get ready! SLIP RIGHT! Dodge it! Now... SLIP LEFT! Good!',
      },
      {
        time: 709,
        text: 'DUCK! Bend those knees! Now... ROLL RIGHT! Get under it!',
      },
      {
        time: 718,
        text: 'ROLL LEFT! Perfect! Get ready... PARRY! Tap it away!',
      },
      {
        time: 727,
        text: 'CATCH! Catch it in your palm! Now... STEP BACK! Create that space!',
      },
      {
        time: 736,
        text: 'SIDE STEP LEFT! Good! SIDE STEP RIGHT! Awesome!',
      },
      {
        time: 745,
        text: 'ANGLE LEFT! Cut that corner! Now... ANGLE RIGHT! Keep those feet quick and your hands high!',
      },
      {
        time: 756,
        text: "Let's do a few more, faster! SLIP! DUCK! PARRY! CATCH! STEP BACK! You're a defensive superstar!",
      },
      {
        time: 768,
        text: "Incredible reactions! For our final defensive lesson, we will become a fortress. We're going to use the Cover moves to build the ultimate Turtle Shell!",
      },
      {
        time: 779,
        text: "First, let's practice our covers. Shut the top windows on your head! Right temple, COVER! Left temple, COVER! Good!",
      },
      {
        time: 789,
        text: 'Now shut the bottom windows on your body! Right ribs, elbow down! Left ribs, elbow down! Perfect!',
      },
      {
        time: 800,
        text: 'Now, bring it all together to make your Turtle Shell. Squeeze your elbows into your ribs, bring your fists tight to your cheeks, and tuck your chin. You are now a fortress. Nothing can get through!',
      },
      {
        time: 812,
        text: "Hold that strong shell! Breathe. For 5... 4... 3... 2... 1... Now, stay in your shell and just do a tiny 'wobble' by bending your knees. It makes you an even harder target! Wobble, wobble, wobble...",
      },
      {
        time: 825,
        text: 'And FREEZE! Shake it out. That is some pro-level defense! Get back in your perfect stance: two feet on train tracks, knees soft, and shoulders down. Amazing work!',
      },

      // 33:00-36:00 Reaction game 2 with footwork
      { time: 936, text: 'Coach call with feet. Slip right, then step right.' },
      { time: 948, text: 'Slip left, then step left.' },
      { time: 960, text: 'Duck, then step back.' },
      { time: 972, text: 'Parry, then angle left.' },
      { time: 984, text: 'Catch, then angle right.' },
      { time: 996, text: 'Roll right, then small pivot.' },
      { time: 1008, text: 'Roll left, then small pivot.' },
      { time: 1020, text: 'Reminder. Feet first, then guard back up.' },

      // 36:00-39:00 Defense chains
      { time: 1032, text: 'Chain. Slip right, slip left, guard. Do 2.' },
      { time: 1044, text: 'Chain. Duck, roll right, guard. Do 2.' },
      { time: 1056, text: 'Chain. Parry, catch, guard. Do 2.' },
      { time: 1068, text: 'Chain. Step back, side step left, guard. Do 2.' },
      { time: 1080, text: 'Chain. Angle right, high cover, guard. Do 2.' },
      {
        time: 1092,
        text: 'Reminder. Hands home after every move. Breathe out softly.',
      },

      {
        time: 936,
        text: "Alright champion, let's move on to some pro-level drills! We're going to combine our defense with our footwork. This is how you defend and disappear at the same time! First up, we'll link the Slip with a Side Step.",
      },
      {
        time: 947,
        text: "We're going to slip to the right to make the punch miss, and then immediately take a side step to the right to get out of danger! Let's drill it for 20 seconds! Ready? Go!",
      },
      {
        time: 957,
        text: "SLIP right, STEP right! Make the punch miss, then glide away! Now let's go the other way! SLIP left, STEP left! Keep your hands up the whole time!",
      },
      {
        time: 969,
        text: "Excellent! Now let's link the Duck with a Step Back. Get under the punch, then create space! Ready? For 20 seconds, let's drill it! DUCK... and STEP BACK!",
      },
      {
        time: 980,
        text: 'Bend your knees to get under the shot, then immediately push off your front foot to glide back. Keep doing it! Duck and escape! Ten seconds left!',
      },
      {
        time: 992,
        text: "Awesome! Now let's parry and create an angle. We're going to PARRY with the front hand, then take a TRIANGLE STEP to the side. Ready? Let's drill it!",
      },
      {
        time: 1003,
        text: "Parry the punch away, then immediately cut the corner with your feet! You're redirecting their punch while setting up your own! Parry and Angle!",
      },
      {
        time: 1014,
        text: "Great work! Let's do one more set! Roll to the right, then do a small pivot. Roll under the punch and then spin to a new angle! Roll and Pivot!",
      },
      {
        time: 1026,
        text: "Incredible work! Your feet and head are working together! Now, let's drill some fast defensive chains. The goal is to flow from one move to the next without pausing. First chain: Slip Right, Slip Left.",
      },
      {
        time: 1038,
        text: "Let's do it for 20 seconds. Find a rhythm. Ready? Go! Slip right, slip left... then reset your guard. Again! Slip right, slip left... reset. Keep your eyes forward!",
      },
      {
        time: 1050,
        text: "Excellent! New chain: Duck, then Roll Right. Get under the first punch, then roll under the second! Ready? Let's drill it! DUCK... ROLL RIGHT... and reset! Again! DUCK... ROLL RIGHT!",
      },
      {
        time: 1063,
        text: 'Perfect! Now a hand-defense chain: Parry, then Catch. Both with the front hand! Ready? PARRY... CATCH... reset! Again! PARRY... CATCH... reset!',
      },
      {
        time: 1075,
        text: 'Great! Now a footwork chain: Step back, then Side Step Left. Create space, then get out of the way! Ready? STEP BACK... STEP LEFT... reset! Again! STEP BACK... STEP LEFT!',
      },
      {
        time: 1087,
        text: "Final chain, let's put it all together! Angle Step Right, then a High Cover. Cut the corner, then protect your head! Ready? ANGLE RIGHT... HIGH COVER... reset! One more time! ANGLE RIGHT... HIGH COVER!",
      },
      {
        time: 1100,
        text: "Amazing chains! Remember to always bring your hands home to your cheeks after every single move. Let's take a deep breath... 1, 2, 3, 4, 5...great session! See you next time!",
      },
    ],
    timeTotal: 1360,
    equipment: 'None',
  },
  // INTERMEDIATE BOXING DEFENSE (45 minutes) done
  {
    id: 2097,
    name: 'Intermediate Boxing Defense',
    categories: ['Defense', 'intermediate'],
    sports: ['boxing'],
    difficulty: 'intermediate',
    skill: 'Defense',
    points: 28,
    instructions:
      "Defense wins championships! In this workout, you'll move beyond just blocking and learn how to become a defensive superstar. We'll master head movement, learn how to counter-punch out of every defensive move, and become impossible to hit. Let's build your fortress!",
    timedInstructions: [
      {
        time: 0,
        text: "Alright Champion, welcome to your intermediate defense masterclass. Today, we stop being a punching bag and start being a fortress. We're going to learn how to make them miss, and make them pay. Let's get to work!",
      },
      {
        time: 10,
        text: "Let's warm up. Get in your stance, guard up, and start with a light boxer's bounce. Today's warm-up is all about head movement, the secret to becoming untouchable.",
      },
      {
        time: 20,
        text: "First, let's master the Slip. This is how you make a straight punch miss by just an inch. The secret is, you don't move your feet. You just bend your knees and move your head off the center line.",
      },
      {
        time: 31,
        text: "Let's practice that slowly. To slip to your right, just bend your legs and dip your head a few inches to the right, like your ear is trying to touch your shoulder. Then come right back to the middle. Let's try the other side. Slip left... and back to the middle. Keep your eyes forward.",
      },
      {
        time: 44,
        text: "Perfect! Now let's find a rhythm and drill that for 20 seconds. Just slip right, then slip left. Keep your hands glued to your cheeks. Ready? Go! Right... left... right... left. You're dodging laser beams!",
      },
      {
        time: 65,
        text: "Awesome slips! Now for the Roll. This is for dodging bigger, swinging punches. For this, you're going to draw the letter 'U' with your head, using your legs to power the movement.",
      },
      {
        time: 76,
        text: "Let's walk through it. To roll to your right, you'll bend your knees to go down, sweep your head under an imaginary swinging rope, and come up smoothly on the other side. Perfect! Now let's roll to the left. Down, under, and up. Feel that U-shape?",
      },
      {
        time: 80,
        text: "Okay, let's drill the roll for 20 seconds. Find a smooth rhythm, like you're rolling under waves. Ready? Go! Roll right... and roll left. Keep those hands high! You're moving like a pro!",
      },
      {
        time: 82,
        text: "Excellent! Now that our head movement is warm, let's link it to our punches for the next minute. Remember, after you punch, you must move your head. I'll call it out. Go!",
      },
      {
        time: 88,
        text: 'Give me a one-two! JAB-CROSS! And right after, SLIP RIGHT! Perfect! Now just a front-hand JAB! And now ROLL LEFT!',
      },
      {
        time: 92,
        text: "That's it! Punch and move! Never be a statue after you punch. Let's do another one-two! JAB-CROSS! And now SLIP-SLIP! Left and right!",
      },
      {
        time: 95,
        text: "Great flow! You're halfway there, keep those hands and feet moving! Give me another JAB! And now ROLL RIGHT! Perfect!",
      },
      {
        time: 100,
        text: 'Ten more seconds! Pick up the pace a little! Punch and move, punch and move! 5... 4... 3... 2... 1...',
      },
      {
        time: 112,
        text: 'TIME! Great warm-up.',
      },
      {
        time: 142,
        text: "Listen up. This first round is about mastering head movement chains. We're going to link our slips and rolls together to become impossible to hit. No punching, just pure defense. Get ready.",
      },
      {
        time: 172,
        text: "Round One is all about head movement! Three minutes of non-stop work! We're going to become impossible to hit by mastering the Slip and the Roll. Get in your stance, guard high! Get ready!",
      },
      {
        time: 180,
        text: 'First minute: we are drilling the Slip, side to side. Just focus on slipping for the whole minute! Ready? 3, 2, 1, Go!',
      },
      {
        time: 189,
        text: "Let's start the rhythm using the name. Slip right... slip left... Good. Again. Slip right... slip left. Get used to that tempo.",
      },
      {
        time: 200,
        text: "Now, let's use the action. Dip your head to the right, just off the center line... and now dip it to the left. Remember, the movement comes from your knees and your waist, not from bending your back.",
      },
      {
        time: 212,
        text: "Excellent! Halfway through the minute! Now let's use the analogy. Dodge that laser beam to your right... and dodge the laser beam to your left! Keep your hands super-glued to your cheeks.",
      },
      {
        time: 225,
        text: "Keep going! Now I'm going to mix it up! Slip right! Now, dip left! Dodge right! Make the punch miss to the left! Ten seconds to go!",
      },
      {
        time: 236,
        text: 'Give me your sharpest ones yet! 5... 4... 3... 2... 1...',
      },
      {
        time: 242,
        text: "Great slips! Right into minute two! No rest! Now we're drilling the Roll. The combo is Roll Right, then Roll Left for the entire minute. Let's go!",
      },
      {
        time: 252,
        text: "Let's start with the name. Roll right... and roll left. Stay low, get under those imaginary hooks. Feel that U-shape.",
      },
      {
        time: 263,
        text: "Now, let's describe the action. Get under the punch to your right... and now get under the punch to the left. Use your legs to power the movement, this builds strength!",
      },
      {
        time: 275,
        text: "Perfect! Halfway through the minute! Let's use the analogy. Draw that big 'U' shape with your head to the right... and now draw the 'U' to the left. Keep your eyes forward the whole time.",
      },
      {
        time: 288,
        text: "You're doing great! Let's mix up the cues! Roll right! Now, get under the punch to the left! Draw a 'U' to the right! Ten seconds to go!",
      },
      {
        time: 299,
        text: 'Keep it smooth! 5... 4... 3... 2... 1...',
      },
      {
        time: 305,
        text: "Great rolls! Right into our final minute! No rest! We're combining everything! The chain is: Slip Right, Slip Left, Roll Right, Roll Left! Go!",
      },
      {
        time: 316,
        text: "This is the ultimate head movement drill. Let's start with the names. Slip, slip, roll, roll. Find that four-beat rhythm.",
      },
      {
        time: 328,
        text: "Now with mixed cues! Dodge right, dip left, get under it to the right, and draw a 'U' to the left! You are a ghost in there, impossible to hit!",
      },
      {
        time: 340,
        text: "Halfway through the final minute! Don't get sloppy now, this is where it counts! Keep your hands glued to your cheeks! Slip, slip, roll, roll!",
      },
      {
        time: 352,
        text: 'Your legs might be getting tired, but push through it! This is what makes a champion! Final ten seconds! Your fastest chain yet! Go, go, go!',
      },
      {
        time: 365,
        text: 'TIME! Round one is done. Great head movement. One minute of active rest. Shake out your legs, take deep breaths. Stay on your feet.',
      },
      {
        time: 382,
        text: "Listen up. Round Two is where we learn the secret to intermediate defense: the Counter-Punch. This is where we make our opponent pay for trying to hit us. We're going to practice the 'Slip and Rip'—you slip a punch, and you rip a punch right back. This is what separates beginners from champions.",
      },
      {
        time: 398,
        text: "We have three minutes of hard work ahead. The first minute, we're practicing slipping to the right and countering with a power Cross. The second minute, we'll slip left and counter with a Hook. The final minute, we alternate. Get focused. The round starts now!",
      },
      {
        time: 412,
        text: "Let's begin! For one minute, the combo is: Slip to your right, then fire back instantly with a power Cross from your back hand. Go!",
      },
      {
        time: 422,
        text: "Slip and Rip! As you come up from that slip, all that loaded energy explodes into your Cross. Let's find a rhythm. Dip your head right... and explode with that back hand!",
      },
      {
        time: 434,
        text: "Make their punch miss, then land your own. It's one fluid motion. Get your head off the line... then fire the Cross with a power twist! You're halfway through the minute!",
      },
      {
        time: 448,
        text: "Don't just punch with your arm! The power comes from using your legs to push off the ground as you come out of the slip. Let's see it! Slip and POWER!",
      },
      {
        time: 460,
        text: "Ten seconds left on this side! Give me five of your sharpest, most powerful slip-counters yet! Let's go! ONE! TWO! THREE! FOUR! FIVE!",
      },
      {
        time: 472,
        text: "Great! No rest, right into minute two! New combo. Now we're going to Slip to the LEFT, and counter with a front-hand HOOK. Ready? Go!",
      },
      {
        time: 482,
        text: "Dip your head to the left, and as you come back up, swing that front arm around in that perfect 'L' shape for the hook. Let's find the rhythm. Slip... Hook! Slip... Hook!",
      },
      {
        time: 494,
        text: "This is a tough one, it takes a lot of coordination. You're doing great. Get your head off the line, then fire that hook. Make sure your other hand stays glued to your cheek!",
      },
      {
        time: 506,
        text: "We are halfway through this minute! Keep it sharp. Remember the pirate hook analogy! A short, powerful punch. Don't let your arm get lazy.",
      },
      {
        time: 518,
        text: "Ten seconds on this combo! Give me five perfect ones! Let's see that sharp hook! Go! ONE! TWO! THREE! FOUR! FIVE!",
      },
      {
        time: 530,
        text: "Awesome! No rest, right into our final minute! We're alternating! I'm going to call it, you do it! Ready? Let's start! SLIP RIGHT, FIRE THE CROSS!",
      },
      {
        time: 542,
        text: 'Good! Now the other side! DODGE LEFT, COUNTER WITH THE HOOK! Excellent! Back to the first one! DIP RIGHT, EXPLODE WITH THE CROSS!',
      },
      {
        time: 554,
        text: 'Keep it sharp! GET YOUR HEAD OFFLINE LEFT, AND RIP THE HOOK! You are a counter-punching machine! We are halfway there! Thirty seconds left!',
      },
      {
        time: 566,
        text: 'This is where champions are made! Stay focused! Hands high, chin down! SLIP RIGHT, CROSS! And again! SLIP LEFT, HOOK!',
      },
      {
        time: 578,
        text: 'Final ten seconds! Your fastest ones yet! Give me one more of each, super fast! Go, go, go! 5... 4... 3... 2... 1...',
      },
      {
        time: 592,
        text: 'TIME! Excellent counter-punching. One minute rest. That was tough. Grab a sip of water. We have one more technique round.',
      },
      {
        time: 622,
        text: "Listen up. Round Three is all about countering off of your blocks. This is next-level defense. We'll practice taking away their best punches and giving them one of ours right back. First, we'll counter the jab, then we'll counter the power cross. Get ready for three minutes of hard work.",
      },
      {
        time: 637,
        text: "The round starts now! First minute: the Parry-Jab! You're going to use your back hand to parry their jab, and counter instantly with your own front-hand jab. Let's go!",
      },
      {
        time: 648,
        text: "Use your back hand to just tap their jab away... and instantly shoot your own jab back like a laser! It's a battle of the jabs, and yours has to be faster! Let's find a rhythm. Parry-Jab! Parry-Jab!",
      },
      {
        time: 661,
        text: "Keep that parry small and quick! It's a tiny karate chop, not a big swing. Don't reach for it! Let the punch come to you, then redirect it and fire your own shot right down the middle.",
      },
      {
        time: 674,
        text: "You're thirty seconds in, halfway there! Keep your other hand glued to your cheek. Your feet are active and bouncy. This is a speed and timing drill. A small tap, then a lightning-fast jab back.",
      },
      {
        time: 688,
        text: "Fifteen seconds left in this minute! Let's pick up the pace! Parry-Jab! Parry-Jab! Parry-Jab! Give me five more sharp ones! 5... 4... 3... 2... 1...",
      },
      {
        time: 700,
        text: "Great! No rest, right into minute two! New combo. We're going to use a High Block with our front hand to stop their cross, then fire our own power Cross back. Go!",
      },
      {
        time: 712,
        text: "Bring that front hand to your forehead like a shield to block their power punch... absorb the impact... and then explode back with your own power Cross! Let's get the rhythm. Block... Cross! Block... Cross!",
      },
      {
        time: 725,
        text: 'This is a power-for-power move. You block their best shot and give them yours right back! Make sure you twist your body on that cross and squish the bug for maximum power!',
      },
      {
        time: 738,
        text: "Halfway through this minute! Keep that block strong! Don't let your hand drift away from your forehead. Keep it tight! Then unleash that power punch!",
      },
      {
        time: 751,
        text: "Fifteen seconds left! Let's see five powerful Block-Crosses! Make them count! ONE! TWO! THREE! FOUR! FIVE!",
      },
      {
        time: 763,
        text: "Excellent! No rest, right into our final minute! We're alternating between the two combos! Listen for my call! Ready? PARRY-JAB!",
      },
      {
        time: 774,
        text: 'Good, use your speed! Now the power combo! BLOCK-CROSS! Perfect! Back to speed! PARRY-JAB! And back to power! BLOCK-CROSS!',
      },
      {
        time: 788,
        text: "You are a defensive superstar! You have an answer for both of their punches! We're halfway through the final minute! Don't get lazy on your defense now!",
      },
      {
        time: 801,
        text: 'Make every block and parry perfect. Stay sharp! PARRY-JAB! Good! BLOCK-CROSS! Awesome! Fifteen seconds left! Go, go, go!',
      },
      {
        time: 815,
        text: 'Final ten seconds! Give me one more of each, as fast as you can! PARRY-JAB! BLOCK-CROSS! 5... 4... 3... 2... 1...',
      },
      {
        time: 828,
        text: "TIME! Incredible work. One minute rest. That was tough. Grab a sip of water. This is your last rest. After this, it's the Championship Round.",
      },
      {
        time: 862,
        text: "Listen close. This is it. The Championship Round. Three minutes of non-stop action, putting everything we've learned today to the test. Perform every move with speed and precision. Leave everything you've got right here. The round starts now!",
      },
      {
        time: 875,
        text: "First minute, we're drilling our first pro-level combo: Slip right, fire a Cross, then Roll under and come back with a Hook! Let's go!",
      },
      {
        time: 885,
        text: "Let's break it down. First, slip to the right, making that punch miss... as you come up, explode with your power cross... then immediately roll under their next punch... and come up firing your front hook!",
      },
      {
        time: 898,
        text: "That's Slip-Cross-Roll-Hook! A four-part pro combo! Find that fluid motion. You're halfway through the minute. Keep the quality high on every single move!",
      },
      {
        time: 911,
        text: "Don't get sloppy! Keep your hands returning to your guard between each move. Ten seconds left on this combo! Let's see two more perfect ones! Go!",
      },
      {
        time: 923,
        text: 'Great! No rest, right into the second minute! New combo: Parry their jab with your back hand, fire your own Jab, then Slip their cross! Go!',
      },
      {
        time: 934,
        text: 'This is a chess match! Use your back hand to tap their jab away... instantly fire your own jab back to keep them busy... then slip your head to the side to dodge their power hand!',
      },
      {
        time: 946,
        text: "That's Parry-Jab-Slip! A perfect defensive chain! You're thinking like a real fighter now, setting them up and taking away their best punches. Halfway through this minute!",
      },
      {
        time: 959,
        text: "Stay focused, this takes a lot of coordination. A small tap for the parry, a fast snap for the jab, and a clean dip for the slip. Ten seconds, let's see two more!",
      },
      {
        time: 971,
        text: "Awesome! No rest, right into our final minute! We're alternating between those two big combos! I'll call it out! Ready? First one! SLIP, CROSS, ROLL, HOOK!",
      },
      {
        time: 984,
        text: "Perfect! Now the second one! PARRY, JAB, SLIP! Excellent! You're flowing from one to the other!",
      },
      {
        time: 995,
        text: "We are halfway through the final minute! This is where you dig deep! Give it everything you've got! Let's go again! SLIP, CROSS, ROLL, HOOK!",
      },
      {
        time: 1008,
        text: "And the other one! PARRY, JAB, SLIP! Fifteen seconds left! Don't stop now! Push through it!",
      },
      {
        time: 1018,
        text: 'Final ten seconds! Your fastest, sharpest combos of the day! Go, go, go! 5... 4... 3... 2... 1...',
      },
      {
        time: 1028,
        text: "TIME! YOU DID IT! That was a masterclass in defense! I am seriously impressed. Let's cool down. You've earned it.",
      },
      {
        time: 1038,
        text: 'Just walk around slowly in your space for a moment. Take deep, slow breaths. In through your nose... and out through your mouth. Let that heart rate come down.',
      },
      {
        time: 1050,
        text: "Great. Now, let's stretch your neck from all that head movement. Take a deep breath, and as you exhale, gently tilt your right ear towards your right shoulder. You should feel a nice, easy stretch all along the left side. Don't pull, just relax into it.",
      },
      {
        time: 1065,
        text: "And slowly come back to the center. Let's do the other side. As you breathe out, gently tilt your left ear towards your left shoulder. Hold it there.",
      },
      {
        time: 1078,
        text: "Good. Now let's stretch your back and core. Stand with your feet wide and gently twist your whole upper body from side to side. Let your arms be loose and just follow the motion.",
      },
      {
        time: 1090,
        text: "Now, let's stretch those shoulders. Take your right arm and gently pull it across your body with your left arm, giving it a good hug. You should feel that stretch in the back of your shoulder.",
      },
      {
        time: 1102,
        text: 'And switch sides. Bring your left arm across and give it a hug with your right. Hold it for a few more seconds.',
      },
      {
        time: 1112,
        text: 'Finally, clasp your hands behind your back. Squeeze your shoulder blades together and push your chest out and up towards the ceiling. Feel that great stretch across your chest.',
      },
      {
        time: 1124,
        text: "And relax. Incredible work today, Champion. You learned how to not just defend, but to counter-punch like a pro. Be proud of that. Make sure you drink plenty of water. You've earned it.",
      },
    ],
    timeTotal: 1672,
    equipment: 'None',
  },
  // ADVANCED BOXING DEFENSE (45 minutes) Done
  {
    id: 2098,
    name: 'Advanced Boxing Defense',
    categories: ['Defense', 'advanced'],
    sports: ['boxing'],
    difficulty: 'advanced',
    skill: 'Defense',
    points: 28,
    instructions:
      "Ready to become a defensive ghost? This workout is for advanced boxers who want to be impossible to hit. We'll learn to layer our defenses, defend while moving, and unleash devastating counter-combinations. Let's build an impenetrable wall.",
    timedInstructions: [
      {
        time: 0,
        text: "Alright Champion, welcome to your advanced defense masterclass. Today, we're going beyond single moves and learning how to chain our defense together like a pro. Let's get to work.",
      },
      {
        time: 10,
        text: "Let's start the warm-up with a high-energy defensive flow drill. For the next two minutes, the goal is to be in constant motion. Get in your stance, guard up, let's begin!",
      },
      {
        time: 20,
        text: "For the first thirty seconds, let's just focus on the feet. Start with a light shuffle, side to side. Left... right... good. Now shuffle forward and back, staying on your toes.",
      },
      {
        time: 32,
        text: "Excellent. Now, let's mix in the pivot. Shuffle... and now pivot to the right by turning on the ball of your front foot and swinging your back foot around! Perfect. Shuffle again... and now pivot left the other way!",
      },
      {
        time: 45,
        text: "Thirty seconds down! Now, keep those feet moving, but let's add head movement. As you're shuffling, practice your slip by dipping your head to the left off that center line... now dip it to the right!",
      },
      {
        time: 58,
        text: "That's it! Now, after you pivot, practice the roll! Pivot by turning on that front foot... and then roll under an imaginary punch by drawing that big 'U' shape with your head!",
      },
      {
        time: 71,
        text: 'One minute down, one to go! Now we add light punches. Remember the rule: move your head or feet AFTER you punch. Throw a quick one-two... and then slip to the right by dipping that shoulder!',
      },
      {
        time: 84,
        text: 'Perfect! Now, shuffle left... and snap out a jab! Good. Now, pivot right by turning on that front foot... and fire a one-two combo as soon as you land!',
      },
      {
        time: 97,
        text: "Final thirty seconds! Let's put it all together in a freestyle flow! Shuffle, pivot, and slip! Remember to dip that head off the line. Go, go, go!",
      },
      {
        time: 109,
        text: "Be a blur! Now roll under a hook by drawing that 'U' shape with your head! Good! Fire a jab! Pivot to a new angle by turning on that front foot! Fifteen seconds left!",
      },
      {
        time: 121,
        text: "Ten more seconds! Give me everything you've got! Fast feet, fast head movement! 5... 4... 3... 2... 1...",
      },
      {
        time: 130,
        text: 'TIME! Great warm-up. Grab a quick sip of water. We have five 3-minute rounds of hard work ahead. The first round is about layering your defense. Get ready.',
      },
      {
        time: 156,
        text: "Round One! Three minutes of hard work! This round is all about layering your defense, combining different moves to create an unbreakable wall. First up, we're drilling the 'Block and Roll'. Let's go!",
      },
      {
        time: 168,
        text: "For this first minute, we're going to practice this one chain. Bring your front hand to your forehead like a shield to BLOCK a straight punch... and right after, bend your knees and draw that 'U' with your head to ROLL under the hook that follows. Let's find the rhythm.",
      },
      {
        time: 180,
        text: 'Block, then Roll! Again! Bring that hand up, then use your legs to get under. This is a realistic combination. You stop their first punch, and you make their second punch miss completely.',
      },
      {
        time: 193,
        text: "You're halfway through the minute! Keep it sharp! A strong block, a deep roll. Your hands come right back to your guard after every single move. Don't get lazy!",
      },
      {
        time: 205,
        text: "Ten seconds left on this combo. Let's see three more perfect ones! Block with the hand, roll with the head! ONE! TWO! THREE!",
      },
      {
        time: 216,
        text: "Great! No rest, right into minute two! New combo: the 'Parry and Slip.' You'll use your back hand to parry their jab, then immediately slip to the side to avoid their cross. Go!",
      },
      {
        time: 228,
        text: "Use your back hand to make that quick little karate chop to PARRY their jab... and as soon as you do, dip your shoulder off the line to SLIP their power punch. Let's get the timing. Parry, then Slip.",
      },
      {
        time: 240,
        text: "This is a high-level skill! You're taking away both of their straight punches. You are in complete control. Keep your other hand glued to your cheek the whole time. You're thirty seconds in!",
      },
      {
        time: 253,
        text: "Don't just move, think about why you're moving. The parry redirects the first punch, the slip dodges the second. Keep drilling it. Parry with the hand, slip with the body.",
      },
      {
        time: 265,
        text: "Ten more seconds on this combo. Stay focused! Let's see three more sharp ones! Parry-Slip! Parry-Slip! Parry-Slip!",
      },
      {
        time: 276,
        text: "Excellent! No rest, right into our final minute! We're alternating between those two chains! Listen for my call! Ready? BLOCK AND ROLL!",
      },
      {
        time: 288,
        text: 'Good! Use your hand and head! Now the other chain! PARRY AND SLIP! Use your hand and body! Perfect!',
      },
      {
        time: 299,
        text: "Keep alternating! BLOCK with your front hand, then ROLL by drawing a 'U'! Now, PARRY with your back hand, then SLIP by dipping your shoulder! You're building a defensive wall!",
      },
      {
        time: 312,
        text: "Thirty seconds left! Don't get tired now! Stay sharp! Make every move count! Your defense is looking incredible!",
      },
      {
        time: 323,
        text: "Fifteen seconds! Let's do one more of each, super fast! BLOCK AND ROLL! Good! PARRY AND SLIP! Awesome!",
      },
      {
        time: 333,
        text: 'Final five seconds! Go, go, go! 5, 4, 3, 2, 1!',
      },
      {
        time: 341,
        text: 'TIME! Round one is done. Excellent work. One minute of active rest. Shake it out, breathe deep. Next round, we defend while moving.',
      },
      {
        time: 396,
        text: "Round Two! Three minutes on defending while moving! This is how you become a true ghost in the ring. First up, we're doing the 'Retreating Slip.' Get ready to create space and make them miss at the same time!",
      },
      {
        time: 408,
        text: "The round starts now! For one minute, you're going to shuffle backward while continuously slipping left and right. Remember, your back foot moves first to go back. Ready? Go!",
      },
      {
        time: 419,
        text: 'Take a small step back, and as you move, dip your head to the left... then take another step back and dip your head to the right. Your feet and your head are working as a team!',
      },
      {
        time: 431,
        text: "Keep that rhythm. Step-slip, step-slip. Your head is constantly moving as your feet are gliding back. You're halfway through the minute! Keep that guard super-glued to your cheeks!",
      },
      {
        time: 444,
        text: "This is how you get out of trouble safely, by moving backward and making yourself a smaller target. Fifteen seconds left! Let's pick up the pace a little!",
      },
      {
        time: 454,
        text: 'Ten seconds! Stay coordinated! Quick feet, quick slips! 5, 4, 3, 2, 1!',
      },
      {
        time: 461,
        text: "Great! No rest, right into minute two! The 'Advancing Roll!' Now you're going to shuffle FORWARD while rolling left and right. This is how you close the distance safely. Go!",
      },
      {
        time: 473,
        text: "Shuffle forward by stepping with your front foot first... and as you move, draw that 'U' shape with your head to roll under their punches. You are walking them down behind a wall of head movement!",
      },
      {
        time: 485,
        text: "Keep the rhythm. Step-roll, step-roll. This is tough on the legs, but it's an incredible skill for getting on the inside. Stay low, keep your hands high. Halfway there!",
      },
      {
        time: 499,
        text: "Don't just bend your back, use your legs to power the roll. This is what builds your strength and your defense. Fifteen seconds left!",
      },
      {
        time: 510,
        text: 'Ten seconds! Keep advancing, keep rolling! 5, 4, 3, 2, 1!',
      },
      {
        time: 517,
        text: "Awesome! No rest, right into our final minute! We're putting it all together! I'll call it out, you react! Ready? Let's start with the RETREATING SLIP!",
      },
      {
        time: 528,
        text: 'Shuffle back while dipping your head side to side! Create that space! Make them miss! Keep going!',
      },
      {
        time: 537,
        text: "Good! Now, ADVANCING ROLL! Shuffle forward while rolling your head like you're drawing a 'U'! Close the distance safely!",
      },
      {
        time: 547,
        text: 'Excellent! Back to defense! RETREATING SLIP! Get out of there! Thirty seconds left! You control the distance, you control the fight!',
      },
      {
        time: 559,
        text: 'Now push them back! ADVANCING ROLL! Keep that pressure on! Fifteen seconds left!',
      },
      {
        time: 568,
        text: 'Final ten seconds! Give me your best movement yet! Go, go, go! 5, 4, 3, 2, 1!',
      },
      {
        time: 578,
        text: "TIME! Excellent movement. One minute rest. Now that you can defend, it's time to add the punishment. Next round is all about complex counter-punching.",
      },
      {
        time: 636,
        text: "Round Three! Three minutes of complex counters! This is where we put it all together. First up, the Slip-Cross-Hook. You're going to slip to the right, and counter with a powerful two-punch combo. Let's drill it for one minute. Go!",
      },
      {
        time: 650,
        text: "Dip your head to the right to make them miss... and as you come up from that slip, fire your back-hand Cross, then immediately follow it with your front-hand Hook! It's one fluid motion: Slip, then BAM-BAM!",
      },
      {
        time: 664,
        text: 'This is all about turning their attack into your attack. You use the energy from your slip to power your combination. Keep drilling it. Dodge the punch, then answer back with two of your own!',
      },
      {
        time: 678,
        text: "You're halfway through the minute! Make sure your form is perfect. A sharp dip with your legs for the slip, a powerful twist for the cross, and a tight 'L' shape for the hook!",
      },
      {
        time: 690,
        text: 'Ten seconds left on this side! Give me three more of your sharpest combos yet! Go! Slip-Cross-Hook! Again! One more!',
      },
      {
        time: 699,
        text: "Great! No rest, right into minute two! New combo! Now you're going to Roll to your left, and counter with a Hook-Cross. One minute drill. Let's go!",
      },
      {
        time: 710,
        text: "Draw that 'U' shape with your head to ROLL under their punch to your left... and as you come up on the other side, fire your front-hand Hook, then your back-hand Cross! It's one motion: Roll, then BAM-BAM!",
      },
      {
        time: 724,
        text: "You're using the power from your legs as you come up from the roll to fuel your punches. This is where real knockout power comes from. Let's get that rhythm. Roll... Hook-Cross!",
      },
      {
        time: 738,
        text: "Halfway through this minute! Keep those punches sharp and bring your hands right back to your guard. You're a coiled spring, defending and attacking with power!",
      },
      {
        time: 750,
        text: "Ten seconds! Give me three more powerful combos! Let's see that explosion out of the roll! Go! ONE! TWO! THREE!",
      },
      {
        time: 760,
        text: "Awesome! No rest, right into our final minute! We're alternating between those two big combos! Listen for my call! Ready? Let's start! SLIP RIGHT, and counter with the CROSS-HOOK!",
      },
      {
        time: 773,
        text: "Perfect! Now the other one! ROLL LEFT, and come back with the HOOK-CROSS! Excellent! You're flowing from one to the other!",
      },
      {
        time: 785,
        text: "Make them miss, make them pay! Let's go again! Slip right, Cross-Hook! And now the other way! Roll left, Hook-Cross!",
      },
      {
        time: 798,
        text: "Thirty seconds left! This is advanced stuff and you're crushing it! Stay focused on your form! Keep your defense just as sharp as your offense!",
      },
      {
        time: 810,
        text: "Fifteen seconds! Let's do one more of each, super fast! SLIP, CROSS, HOOK! And... ROLL, HOOK, CROSS!",
      },
      {
        time: 822,
        text: 'Final five seconds! Go, go, go! 5, 4, 3, 2, 1!',
      },
      {
        time: 830,
        text: 'TIME! That was incredible. One minute rest. That was tough. Grab a sip of water. We have one more technique round where we learn a cool, pro-style guard. Then the Championship Round.',
      },
      {
        time: 876,
        text: "Round Four! Three minutes to learn a pro-level stance: the 'Shoulder Roll' guard. This is a tricky, advanced style, so pay close attention. First, get into your normal boxing stance.",
      },
      {
        time: 888,
        text: "Now, to enter the shoulder roll guard, you're going to drop your front hand a little, to about your chest. Bring your back hand up tight to your chin, and most importantly, tuck your chin down behind your front shoulder. Your shoulder is now your main shield.",
      },
      {
        time: 901,
        text: "The main defense here is the shoulder roll. When a punch comes, you twist your torso slightly and let the punch 'roll' off your front shoulder. Let's drill just that rolling motion for one minute. No counters yet. Go!",
      },
      {
        time: 914,
        text: 'Just a small turn of your body. Let your shoulder come up and deflect the imaginary punch. Roll... and reset. Roll... and reset. Your chin must stay hidden and safe behind that shoulder.',
      },
      {
        time: 928,
        text: "You're thirty seconds in. Keep practicing that subtle twist. It's a small, smart movement, not a big, wild one. This is all about being efficient and relaxed.",
      },
      {
        time: 942,
        text: "Fifteen seconds left in this minute. Keep drilling the roll. This is a very advanced skill and you're getting the hang of it. Give me five more sharp rolls!",
      },
      {
        time: 955,
        text: "Great! No rest, right into minute two! Now we add the counter. After you roll, you're in the perfect position to fire back with your back-hand Cross. The combo is: Shoulder Roll, then Cross. Go!",
      },
      {
        time: 969,
        text: "Let the punch slide off your shoulder, and use that turning motion to power your own cross right back at them. It's one fluid move: Roll and Fire! Roll and Fire!",
      },
      {
        time: 982,
        text: "You're halfway through this minute. This is a very tricky style, so be patient with yourself. The goal is to make the defense and offense flow together seamlessly. You're doing great.",
      },
      {
        time: 996,
        text: "Fifteen seconds left. Let's see some powerful counters! Roll and explode with that cross! Give me three more powerful ones! ONE! TWO! THREE!",
      },
      {
        time: 1009,
        text: "Awesome! No rest, right into our final minute! We're going to put it together. We'll do the Shoulder Roll and Cross, and then reset to our NORMAL high guard. This teaches you to switch styles. Go!",
      },
      {
        time: 1022,
        text: 'From your shoulder roll guard... Roll, Cross... then immediately bring both hands back up high to your cheeks in your normal stance. Good. Now, drop back into the shoulder roll guard and do it again.',
      },
      {
        time: 1035,
        text: "This is a super advanced drill. You're learning how to switch between defensive styles in the middle of a fight. You're halfway through the final minute! Stay focused!",
      },
      {
        time: 1048,
        text: "Let's do two more, perfect and clean. Shoulder Roll, Cross... and back to High Guard. One more time! Shoulder Roll, Cross... and High Guard!",
      },
      {
        time: 1060,
        text: "TIME! Awesome job learning a new skill. One minute rest. It's time. The final round. The Championship Round. Everything we've learned today, all in one round. Leave it all in the ring.",
      },
      {
        time: 1120,
        text: "The Championship Round! Three minutes of non-stop, pro-level combinations! Let's GO! First combo: Block and Roll, then counter with a Cross! Let's drill it for 20 seconds!",
      },
      {
        time: 1132,
        text: "Bring your hand up to block, then draw that 'U' with your head to roll, and fire back with that power cross! Keep going!",
      },
      {
        time: 1141,
        text: 'Good! New combo! Parry and Slip, then counter with your own Jab! 20 seconds! Go! Tap their punch away, then make their next one miss, and shoot your own jab!',
      },
      {
        time: 1155,
        text: "Let's move our feet! Retreating Slip for 15 seconds! Create space and make them miss! Go, go, go! Now, Advancing Roll! Close the distance safely!",
      },
      {
        time: 1170,
        text: 'Complex counter time! Slip right, then fire a Cross-Hook combo! 20 seconds of this! Dodge their punch, then make them pay with two of yours! Go!',
      },
      {
        time: 1184,
        text: 'Now the other one! Roll left, then fire a Hook-Cross combo! 20 seconds! Get under their punch and unleash your own power! Go!',
      },
      {
        time: 1198,
        text: 'Drop into your Shoulder Roll guard! For 15 seconds, just roll and counter with the cross! Roll and fire! Show me that pro move!',
      },
      {
        time: 1210,
        text: "Final minute! I'm just going to call moves! You react! Ready? SLIP-SLIP! PARRY! ROLL! BLOCK-CROSS! SLIP-CROSS-HOOK! RETREATING SLIP! You're a defensive machine!",
      },
      {
        time: 1225,
        text: "Thirty seconds left! Give me everything you've got! Move your head, move your feet, counter-punch! Don't get tired now, this is where you prove you're a champion! Go, go, go!",
      },
      {
        time: 1238,
        text: 'Final ten seconds! Make yourself untouchable! 9, 8, 7, 6, 5, 4, 3, 2, 1!',
      },
      {
        time: 1250,
        text: "TIME! YOU DID IT! THAT WAS AN ELITE-LEVEL WORKOUT! I am seriously impressed. Let's cool down. You've earned every second of it.",
      },
      {
        time: 1260,
        text: 'Just walk around slowly for a moment. Take deep, slow breaths. In through your nose... and out through your mouth. Let that heart rate come down.',
      },
      {
        time: 1272,
        text: "Great. Now, let's stretch your neck from all that head movement. Take a deep breath, and as you exhale, gently tilt your right ear towards your right shoulder. You should feel a nice, easy stretch all along the left side. Don't pull, just let gravity do the work.",
      },
      {
        time: 1287,
        text: "And slowly come back to the center. Let's do the other side. As you breathe out, gently tilt your left ear towards your left shoulder. Hold it there.",
      },
      {
        time: 1300,
        text: "Good. Now let's stretch your back and core from all those slips and rolls. Stand with your feet wide and gently twist your whole upper body from side to side. Let your arms be loose and just follow the motion.",
      },
      {
        time: 1312,
        text: "Now, let's stretch those shoulders. Take your right arm and gently pull it across your body with your left arm, giving it a good hug. You should feel that stretch in the back of your shoulder.",
      },
      {
        time: 1324,
        text: 'And switch sides. Bring your left arm across and give it a hug with your right. Hold it for a few more seconds.',
      },
      {
        time: 1334,
        text: 'Finally, clasp your hands behind your back. Squeeze your shoulder blades together and push your chest out and up towards the ceiling. Feel that great stretch across your chest.',
      },
      {
        time: 1346,
        text: "And relax. Incredible work today, Champion. You learned not just how to defend, but how to think and move like a pro. Be proud of that. Drink plenty of water. You've earned it.",
      },
    ],
    timeTotal: 1686,
    equipment: 'None',
  },
  // COMPLETE BOXING (60 minutes) done
  {
    id: 2099,
    name: 'Complete Boxing',
    categories: ['Complete', 'advanced'],
    sports: ['boxing'],
    difficulty: 'advanced',
    skill: 'Complete',
    points: 60,
    instructions:
      "This is it, Champion. The final test. Complete Boxing. Every skill you've learned—offense, defense, and footwork—comes together right now. This is a championship-level workout. Leave everything you've got in the ring. Let's begin.",
    timedInstructions: [
      {
        time: 0,
        text: "Alright Champion, this is it. The final test. Everything we've learned comes together right now. Offense, defense, footwork. This is Complete Boxing. Let's start with a championship-level warm-up.",
      },
      {
        time: 10,
        text: "Get in your stance, guard up. For the next three minutes, we are in a constant flow state. I want you moving your head, your feet, and your hands the entire time. Let's begin.",
      },
      {
        time: 22,
        text: 'Start by shuffling your feet left and right, staying light on your toes. As you move, practice your slip by dipping your head off the center line to the left... and now to the right.',
      },
      {
        time: 35,
        text: "Excellent. Now, practice your pivot by turning on the ball of your front foot and swinging your back foot around... and as soon as you land, roll under an imaginary hook by drawing a big 'U' shape with your head.",
      },
      {
        time: 49,
        text: "One minute down, two to go on this warm-up. Keep that flow going. Your body should be in constant motion. Now, let's layer in the punches. As you shuffle, let's throw a jab by punching your front hand straight out and snapping it back to your cheek.",
      },
      {
        time: 63,
        text: "Great! Now let's combine it. After you pivot by turning on that front foot, I want you to snap that front-hand jab out again! Always move your feet, then punch!",
      },
      {
        time: 75,
        text: "Now let's add the one-two. Throw your front-hand jab, then twist your body and fire the back-hand cross... and immediately roll under a punch by drawing that 'U' with your head. Punch and get out of danger.",
      },
      {
        time: 89,
        text: "Two minutes down, one to go! Let's pick up the pace and throw your full range of punches! When you hear JAB, snap that front hand out! When you hear CROSS, twist and fire that back hand! When you hear HOOK, make that 'L' shape with your front arm and swing it across!",
      },
      {
        time: 104,
        text: "Let's combine everything! Shuffle your feet! Dip your head to slip! Punch your front hand out for a Jab! Twist your body for a Cross! Swing that front arm for a Hook! Be a blur of constant motion!",
      },
      {
        time: 118,
        text: 'Fifteen seconds left! Go, go, go! Pivot on your front foot! Roll with your legs! Slip with your shoulders! Jab! Cross! Hook!',
      },
      {
        time: 129,
        text: 'Final five seconds! 5, 4, 3, 2, 1!',
      },
      {
        time: 135,
        text: 'TIME! Great warm-up. Grab a quick sip of water. We have five hard rounds ahead. The first round is all about offensive combinations. Get ready to let your hands go.',
      },
      {
        time: 180,
        text: "Round One! Three minutes of pure offense! This is all about letting your hands go in long, powerful combinations. First up, the four-punch combo: Jab-Cross-Hook-Cross. We're drilling it for one minute straight. Ready? 3, 2, 1, Go!",
      },
      {
        time: 192,
        text: "Let's build it! Snap your front hand out for the JAB... then twist your body for the back-hand CROSS... swing your front arm in that 'L' shape for the HOOK... and finish with another power CROSS! Let's find that rhythm!",
      },
      {
        time: 205,
        text: "Jab-Cross-Hook-Cross! It's a constant attack! Keep your guard high between each punch. Don't get sloppy. You're halfway through the minute! Stay strong!",
      },
      {
        time: 218,
        text: 'Remember your form on every punch. A fast snap for the jab, a full body twist on the cross, a tight elbow on the hook. Fifteen seconds left!',
      },
      {
        time: 229,
        text: "Ten seconds! Let's see two more perfect, powerful combos! Go! One more! Go!",
      },
      {
        time: 237,
        text: 'Great! No rest, right into minute two! New combo: the Jab-Jab-Cross. A fast double jab to set up the power shot. One minute of this! Go!',
      },
      {
        time: 249,
        text: 'The secret is a quick reset between the jabs. Snap your front hand out... bring it right back to your cheek... and snap it out again! THEN, unleash the power CROSS with a big twist! Snap-snap-BOOM!',
      },
      {
        time: 263,
        text: "Keep that double jab fast and light! It's designed to blind your opponent and set up the big punch. You're halfway there! Keep your back hand glued to your cheek until it's time to fire.",
      },
      {
        time: 277,
        text: "Fifteen seconds left! Let's pick up the pace! Jab-Jab-Cross! Jab-Jab-Cross! Give me three more lightning-fast ones!",
      },
      {
        time: 288,
        text: "Awesome! No rest, right into our final minute! We're alternating! Ready? Let's start with the long combo! JAB with the front hand, CROSS with the back, HOOK with the front, CROSS with the back!",
      },
      {
        time: 302,
        text: 'Perfect! Now the other one! Snap the JAB out twice, then fire the power CROSS! Good! Back to the first combo! Jab-Cross-Hook-Cross!',
      },
      {
        time: 316,
        text: "Keep that pressure on! You are the attacker this round! Let your hands go, but keep your form clean. Thirty seconds left! Don't slow down!",
      },
      {
        time: 329,
        text: 'Fifteen seconds! This is where you dig deep! Non-stop punching! Go, go, go! 5, 4, 3, 2, 1!',
      },
      {
        time: 341,
        text: 'TIME! Great offensive round. One minute rest. Shake out your arms. Now, we become a fortress. Next round is three minutes of pure, pro-level defense.',
      },
      {
        time: 401,
        text: "Round Two! Three minutes of pure defense! First minute, we're drilling a complex chain: Parry, Slip, then Roll. Get ready to be untouchable! Go!",
      },
      {
        time: 413,
        text: "Here's the combo: Use your back hand to tap away their jab with a PARRY... immediately dip your head to the side to SLIP their cross... then draw that 'U' with your head to ROLL under the hook!",
      },
      {
        time: 426,
        text: "Let's drill it! Parry-Slip-Roll. You are defending against their entire combination. This is pro-level stuff. Stay focused. Halfway through the minute!",
      },
      {
        time: 440,
        text: "Keep your hands high and your eyes forward. The parry is small, the slip is sharp, the roll is smooth. Fifteen seconds left! Let's see two more perfect chains!",
      },
      {
        time: 453,
        text: "Great! No rest, right into minute two! Now we add footwork. The combo is: Step Back, High Block, then Pivot out. You're creating space, blocking, and disappearing. Go!",
      },
      {
        time: 466,
        text: 'Take a quick shuffle back with your feet to get away... as you land, bring your front hand to your forehead to BLOCK their punch... then immediately turn on your front foot to PIVOT to safety.',
      },
      {
        time: 479,
        text: "Let's get the rhythm! Escape, block, disappear! This is smart boxing. You're never where they expect you to be. Halfway through the minute!",
      },
      {
        time: 492,
        text: "Keep every movement sharp and for a reason. A quick step back, a strong block, a fast pivot. Fifteen seconds left! Let's see two more clean escapes!",
      },
      {
        time: 505,
        text: "Awesome! No rest, right into our final minute! We're alternating! Ready? Let's start with the head movement! PARRY with your hand, SLIP with your body, ROLL with your legs!",
      },
      {
        time: 518,
        text: 'Excellent! Now the footwork combo! STEP BACK to escape, BLOCK with your shield, PIVOT to disappear! Perfect!',
      },
      {
        time: 529,
        text: "Back to the first one! PARRY-SLIP-ROLL! You're a defensive ghost! Now the second! STEP BACK, BLOCK, PIVOT! You're a strategic genius!",
      },
      {
        time: 542,
        text: "Thirty seconds left! Don't get tired now! Stay sharp! Make every move count! Your defense is an unbreakable wall!",
      },
      {
        time: 555,
        text: 'Fifteen seconds! One more of each, super fast! Go! PARRY-SLIP-ROLL! Now! STEP BACK, BLOCK, PIVOT! Go!',
      },
      {
        time: 568,
        text: '5, 4, 3, 2, 1! TIME!',
      },
      {
        time: 630,
        text: "Round Three! Three minutes of complex counter-punching! You've learned to defend, now you'll learn to make them pay with powerful combinations. First up, the Slip-Cross-Hook-Cross. One minute drill. Let's go!",
      },
      {
        time: 643,
        text: "Here's the combo: first, dip your head to the right to SLIP their punch... as you come up from that slip, you're going to fire back with a back-hand CROSS, then a front-hand HOOK, and finish with another back-hand CROSS!",
      },
      {
        time: 657,
        text: "Let's get the rhythm. Slip, then BAM-BAM-BAM! This is about turning their attack into your attack. You use the energy from your slip to power that entire three-punch combination. Let's drill it.",
      },
      {
        time: 671,
        text: "Halfway through the minute! Keep that combo sharp! A good slip with your legs, a sharp twist on the cross, a tight 'L' shape on the hook, and another powerful twist on the final cross! Every part matters!",
      },
      {
        time: 684,
        text: "Fifteen seconds left! Let's see two more of your sharpest, most powerful combos yet! Go! Slip-Cross-Hook-Cross! One more!",
      },
      {
        time: 694,
        text: "Great! No rest, right into minute two! New combo! Now you're going to Roll to your left, and counter with a Hook-Cross-Hook. One minute drill. Let's go!",
      },
      {
        time: 706,
        text: "Draw that 'U' shape with your head to ROLL under their punch to your left... and as you come up on the other side, you're going to fire your front-hand HOOK, then your back-hand CROSS, and finish with another front-hand HOOK!",
      },
      {
        time: 720,
        text: "This is a real power combination! You're using the momentum from the roll to fuel all three punches. Let's get the rhythm. Roll, then BAM-BAM-BAM! Stay low on the roll and explode upwards into the punches!",
      },
      {
        time: 734,
        text: "Halfway through this minute! Keep those punches sharp and bring your hands right back to your guard after each one. You're a coiled spring, defending and attacking with power!",
      },
      {
        time: 747,
        text: "Fifteen seconds left! Let's see two more powerful combos! Let's see that explosion out of the roll! Go! ONE! And TWO!",
      },
      {
        time: 758,
        text: "Awesome! No rest, right into our final minute! We're alternating between those two big combos! Listen for my call! Ready? Let's start! SLIP RIGHT, and counter with the CROSS-HOOK-CROSS!",
      },
      {
        time: 771,
        text: "Perfect! Now the other one! ROLL LEFT, and come back with the HOOK-CROSS-HOOK! Excellent! You're flowing from one to the other!",
      },
      {
        time: 783,
        text: "Make them miss, make them pay! Let's go again! Dip your head to the right and fire that three-punch combo! Good! Now draw that 'U' to the left and unleash the other one!",
      },
      {
        time: 796,
        text: "Thirty seconds left! This is advanced stuff and you're crushing it! Stay focused on your form! Keep your defense just as sharp as your offense!",
      },
      {
        time: 808,
        text: "Fifteen seconds! Let's do one more of each, super fast! SLIP, CROSS, HOOK, CROSS! And... ROLL, HOOK, CROSS, HOOK!",
      },
      {
        time: 820,
        text: 'Final five seconds! Go, go, go! 5... 4... 3... 2... 1...',
      },
      {
        time: 828,
        text: "TIME! Unbelievable work. One minute rest. We've done offense, defense, and counters. Now it's time to focus on controlling the ring with your feet.",
      },
      {
        time: 855,
        text: "Round Four! Three minutes of advanced footwork and punching! The golden rule is: move your feet first, then let your hands go. First minute, we're drilling the Angle Step into a Jab-Cross. Let's go!",
      },
      {
        time: 868,
        text: "Here's the combo: First, cut the corner by taking that Triangle Step to the left... as soon as your back foot lands, snap out the front-hand JAB and then twist your body to fire the back-hand CROSS!",
      },
      {
        time: 881,
        text: "Let's find the rhythm. Step-and-fire! Move your feet to create a new angle, then punch down that new line. Keep your guard high as you step. You're halfway through the minute!",
      },
      {
        time: 895,
        text: "This is how you find openings on an opponent. You're never in the same place twice. Fifteen seconds left! Let's see three more sharp angle attacks! Go!",
      },
      {
        time: 908,
        text: "Great! No rest, right into minute two! New combo: the Pivot Hook. You're going to spin to a new angle and surprise them with a hook. Go!",
      },
      {
        time: 920,
        text: "Turn on the ball of your front foot to PIVOT... and as you finish the spin, unleash that front-arm HOOK, making sure to keep that 'L' shape! The pivot adds secret power to the punch.",
      },
      {
        time: 934,
        text: "This is a great way to counter someone who is rushing at you. You turn, and they run right into your punch. Let's keep drilling it. Pivot... then Hook! Halfway there!",
      },
      {
        time: 948,
        text: "Stay balanced as you turn! Keep your knees bent. Let's see three more perfect pivot-hooks! ONE! TWO! THREE!",
      },
      {
        time: 960,
        text: "Awesome! No rest, right into our final minute! We're alternating! Ready? First, ANGLE LEFT, then fire that JAB-CROSS down the new line!",
      },
      {
        time: 973,
        text: 'Perfect! Now the other one! PIVOT RIGHT, and let that front HOOK fly as you turn! Excellent!',
      },
      {
        time: 984,
        text: "One move to attack, one move to counter! Show me both! You're controlling the entire ring with your feet! Thirty seconds left! Don't slow down!",
      },
      {
        time: 997,
        text: 'Fifteen seconds! Be explosive! Move your feet with purpose! Give me one more of each, super fast! Go, go, go! 5, 4, 3, 2, 1!',
      },
      {
        time: 1010,
        text: "TIME! Incredible footwork. One minute rest. This is it. The final round. The Championship Round. Put everything together. Show me what you've got.",
      },
      {
        time: 1070,
        text: "The Championship Round! Three minutes! No set combos, I'm going to coach you through situations like a real fight. Listen close and react! Let's GO!",
      },
      {
        time: 1082,
        text: "Alright, first minute, establish control. Circle to your left, using your shuffle steps. As you move, snap your front hand out in a sharp JAB. Control the distance. Don't let them get set.",
      },
      {
        time: 1095,
        text: "Okay, they're throwing a jab! Use your back hand to PARRY it away, and fire your own JAB right back! Good! Now they're throwing a one-two! PARRY their jab, then SLIP your head to the side to make their cross miss! Take away their best punches!",
      },
      {
        time: 1111,
        text: "You've got them thinking! Now pressure them! Shuffle forward and throw a one-two! Snap that jab, twist for the cross! Now cut the corner with a Triangle Step and throw another one-two from the new angle!",
      },
      {
        time: 1126,
        text: "One minute down, two to go! They're throwing a big hook at you! ROLL under it by drawing that 'U' shape with your head... and as you come up, make them pay with a HOOK-CROSS counter! Yes!",
      },
      {
        time: 1140,
        text: "They're backing up! Hunt them down! Shuffle forward, snapping that jab to keep them on their heels. Don't let them rest for a second!",
      },
      {
        time: 1152,
        text: "They're trying to trap you in the corner! PIVOT out to your right! Turn on that front foot and spin out to safety! Circle away! That's smart boxing!",
      },
      {
        time: 1164,
        text: "Two minutes down, one to go! This is where you dig deep! You're tired, but you're a champion! Let's see your best power combo! That's Jab-Cross-Hook-Cross! Go!",
      },
      {
        time: 1177,
        text: 'Now get defensive! Make them miss! SLIP right, SLIP left, then ROLL under! Perfect! Fire back with a single power CROSS!',
      },
      {
        time: 1189,
        text: 'Thirty seconds left! Empty the tank! Let your hands go! Non-stop combinations, any ones you want! Go, go, go!',
      },
      {
        time: 1202,
        text: "Fifteen seconds! This is for the championship! Push the pace! Don't stop punching until you hear that bell! 10, 9, 8, 7, 6, 5, 4, 3, 2, 1!",
      },
      {
        time: 1218,
        text: 'TIME! YOU DID IT! THAT WAS A CHAMPIONSHIP PERFORMANCE! Unbelievable work. Let your body relax. We have a final burnout, then a long cool-down.',
      },
      {
        time: 1245,
        text: "Just a short rest. For our final burnout, we're going back to basics. This is two minutes of non-stop one-twos. No thinking, just pure conditioning and heart. Ready to empty the tank?",
      },
      {
        time: 1258,
        text: "Two minutes on the clock! Let's GO! Snap your front hand out for the Jab, then twist your body for the back-hand Cross! Find a steady rhythm you can keep for the whole two minutes.",
      },
      {
        time: 1272,
        text: "This is all heart! Every punch is clean. The jab is fast, the cross is powerful. Keep your other hand glued to your cheek. Thirty seconds down, you're doing great!",
      },
      {
        time: 1286,
        text: "Don't get sloppy, even when you're tired. Keep that form perfect. Breathe with every punch! A sharp breath out on the jab, a bigger one on the cross! Whoosh-WHOOSH!",
      },
      {
        time: 1300,
        text: "One minute down, one to go! You're past the halfway point! Dig deep now! Let's pick up the pace just a little bit. Show me that champion heart!",
      },
      {
        time: 1315,
        text: "Thirty seconds left! This is your final push of the day! Give me everything you've got left! Fast, sharp punches! Don't stop, don't slow down!",
      },
      {
        time: 1328,
        text: 'Fifteen seconds! Go all out! This is for the championship! Go, go, go! 10, 9, 8, 7, 6, 5, 4, 3, 2, 1!',
      },
      {
        time: 1342,
        text: 'TIME! WORKOUT COMPLETE! You are a complete boxer. I am incredibly proud of that effort. See you next time, Champion.',
      },
    ],
    timeTotal: 1745,
    equipment: 'None',
  },
];

export default boxingDrills;
